import {
  type Dispatch,
  type FunctionComponent,
  type PropsWithChildren,
  type ReactNode,
  type SetStateAction,
  type SVGProps,
} from 'react'
import { type Response } from 'request-dot-js'

import { type Timeline } from 'api/clients/ArticleClient'
import { type Favorite } from 'api/clients/FavoriteClient'
import {
  type InformalFeedbackRequest,
  type InformalFeedbackRequestArticle,
  type InformalFeedbackRequestUserCard,
  type ReviewType,
} from 'api/clients/InformalFeedbackRequestsClient'
import { PropertyValue, type Property } from 'api/clients/PropertyClient'
import { type ArticleReaction } from 'api/clients/ReactionClient'
import { type ReadReceiptRequestsAndTasks } from 'api/clients/ReadReceiptClient'
import { type RequestedArticle } from 'api/clients/RequestedArticleClient'
import { type Snippet } from 'api/clients/SnippetClient'
import { type Suggestion } from 'api/clients/SuggestionsClient'
import { type Todo } from 'api/clients/TodoClient'
import { type WorkspaceDomain } from 'api/clients/WorkspaceDomainClientUtils'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { IdeasCount } from 'components/BlazeCommandLine/components/NumberOfIdeasSelect'
import { type DocWithAccesses } from 'components/BulkPermissions/Types'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { Tone } from 'components/DocRightRail/screens/AIAssistant/ToneSelect'
import { IdentityKey, ImageKey } from 'components/SecretIdentityModal/identities'
import { type WebsocketConsumer } from 'providers/WebsocketProvider'
import type * as Actions from 'redux/actions'
import { CollabItem, CollabItemType } from 'stores/DocStore.types'
import { Hue, ITheme, Overrides, ThemePreset } from 'stores/Theme.types'
import { type FontFamilyType } from 'utils/fontUtils'
import { type HandbookTheme } from 'utils/themeUtils'
import { type Project } from './api/clients/ProjectClient'
import { type ReviewPrivacy } from './components/ReviewsWizard/types'
import { type FlaggedFeature } from './hooks/useFlipperFlag'
import { type HighlightPropertiesByNameProps } from './redux/actions/properties'
import { type State } from './stores/Workflows/State'
import { BareArticle } from 'components/CampaignCreationModal/CampaignCreationModal'
import { RevisionAction, RevisionLevel, RevisionType } from 'components/BlazeCommandLine/Types'
import { FontWeightType } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { FontFamilyType as VisualEditorFontFamilyType } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { ScheduledPost, ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { Icon } from 'blaze-ui/icons/Types'
import { DesignChannel as DesignChannelType } from 'components/NewFileModal/Types'
import { BackgroundAssetProps } from 'components/Design/BackgroundsTab/Types'
import { VisualElement } from 'api/clients/VisualElementClient'
import { CrossPostAccount } from 'components/Wizards/Wizard/Form/CrossPostAccountsInput/CrossPostAccountsInput'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { DayGridType } from 'screens/Finder/components/views/CalendarView/utils'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import { DesignPreview } from 'api/clients/DesignClient'
import { SortingDirection } from 'api/clients/FilesViewsClient'
import { AlmanacFileCalendarItem } from 'hooks/queries/calendar/types'
import { BrandPlanScheduleCrosspostInput, VariablesOf } from 'hooks/queries/brandKits/useCreateSchedule'

export type HandbookSubDoc = Pick<DocWithAccesses, 'header_emoji' | 'id' | 'title'>

export enum EducationPopover {
  TrackChanges = 'trackChanges',
  UnauthSuggestion = 'unauthSuggestion',
}

export enum NavSortOrder {
  NameAsc = 'name_asc',
  NameDesc = 'name_desc',
  LastModifiedAsc = 'last_modified_asc',
  LastModifiedDesc = 'last_modified_desc',
  OwnerAsc = 'owner_asc',
  OwnerDesc = 'owner_desc',
}

export enum Teams {
  Members = 'members',
  Groups = 'groups',
}

export type Maybe<T> = T | null

export type HTMLInputEvent = React.ChangeEvent<HTMLInputElement>
export type HTMLSelectEvent = React.ChangeEvent<HTMLSelectElement>
export type HTMLFocusEvent = React.FocusEvent<HTMLDivElement>
export type SetState<T> = Dispatch<SetStateAction<T>>
export type SpanEvent = React.MouseEvent<HTMLSpanElement, MouseEvent> & { target: { textContent: string } }

export type KeyValue<T> = { [key: string]: T }

export interface DbObject {
  id: number
  created_at: string
  updated_at: string
}

export enum Permission {
  Edit = 'edit',
  Comment = 'comment',
  Read = 'read',
  Owner = 'owner',
}

export enum ViewType {
  List = 'list_view',
  Board = 'board_view',
  Table = 'table_view',
  Gallery = 'gallery_view',
  Folder = 'folder_view',
  Calendar = 'calendar_view',
}

export enum CalendarDayGridViewType {
  Month = 'month_view',
  Week = 'week_view',
}

export interface CompanyDetail {
  company_logo: string
  id: number
  logo_url: string
  name: string
  key: string
  invitation_token: string
  invitation_token_enabled: boolean
  invitation_token_expiration_days: number
  image_library_project?: {
    id: number
    key: string
  }
}

export type Avatar = string | null

export interface APICredential {
  id: string
  user_id: number
  secret: string
  expiration_date?: any
  revoked_at?: any
  created_at: string
  updated_at: string
  intended_use?: string
}

export interface AlgoliaContributor {
  first_name: null | string
  last_name: null | string
  id: number
  avatar_url: string
  article_count: number
  copies_count_across_public_docs: number
  shares_count_across_public_docs: number
  likes_count_across_public_docs: number
  objectID: string
  has_public_articles: boolean
  job_search: boolean
  search_job_in_location: string | null
  role: string | null
  skills: string[]
  available_for_hire: boolean
  can_remote: boolean
  can_relocate: boolean
  can_mentor: boolean
  full_time_work_search: boolean
  contractor_work_search: boolean
  experience_level: string | null
  schools: string[]
  experience_in_companies: string[]
  company_name: string | null
  company_role: string | null
  bio: string | null
  url: string
  email: string | null
  intro: string | null
}

export interface AlgoliaPerson {
  user_id: number
  active: boolean
  full_name: string
  timezone: string | null
  location: string | null
  role: string
  company_name: string
  profile_company: number
  profile_image_url: string
}

export interface AlgoliaGroup {
  id: number
  company: number
  name: string
  owners: Array<string>
  members: Array<string>
  emoji: string
}

export interface UserMeta {
  show_onboarding_redirect_modal?: boolean
  [key: string]: boolean | undefined // index signature
}

export interface UserId {
  id: number
}

export interface User extends UserId {
  admin: boolean
  almanac_admin: boolean
  article_ids_of_recent_public_article_likes: number[]
  avatar: Avatar

  // Last workspace id the user was at.
  // Deprecated: Soon to be replaced by Auth#last_workspace_id
  company_id: number

  company_key: string
  company_name: string
  confirmed: boolean
  contributor: boolean
  display_name: string | null
  email: string
  first_name: string | null
  initials: string | null
  last_name: string | null
  meta?: UserMeta
  password_set: boolean
  paying: boolean
  provider: string
  pending_invitation: boolean
  show_almanac_content: boolean
  redirect_to?: string | null
  terms_of_service: boolean
  uid: string
  intercom_hash: string
  onboarded: boolean
}

export interface Category {
  id: number
  name: string
  color_code_light: string
  color_code_dark: string
  featured_collection_ids?: string[]
  active?: boolean
}

export interface CategoryDetail extends Category {
  banner_url: string
  company_id: number
  description: string
  id: number
  name: string
  community_templates_count: number
  show_on_create_from_template: boolean
}

export interface CommentUser {
  id: number
  uuid?: string
  type: UserType
  display_name: string
  initials: string
  avatar: string | null
  avatar_url: string | null
}

export interface Commentable {
  id: number | string
  type?: 'Article' | 'Comment' | 'Todo'
  position?: number
  status?: string
}

export type File = globalThis.File
/*
 * Only 'InformalFeedbackRequest' supported for now.
 */
export enum WorkflowReferenceType {
  InformalFeedbackRequest = 'InformalFeedbackRequest',
}

export interface WorkflowReference {
  entity_id: number
  entity_type: 'Comment' | 'Todo'
  workflow_type: WorkflowReferenceType
  workflow_key: string
  metadata: { [key: string]: any }
}

export enum CommentStatus {
  Published = 'published',
  Resolved = 'resolved',
}

export interface WorkflowReferenceable {
  workflow_references: WorkflowReference[]
}

export interface CommentUpvote {
  created_at: string
  id: number
  user_id: number
}

export interface CommentReaction {
  id: number
  reaction: string
  user_id: number
  user_name: string
}

export interface Comment extends DbObject, WorkflowReferenceable {
  comment: string
  html_comment: string
  commentable_id: Commentable['id']
  commentable_type: Commentable['type']
  important: boolean
  position: number
  status: CommentStatus
  replies: Comment[]
  resolved_at: string | null
  user: CommentUser
  read_status?: boolean
  uuid?: string
  title: string
  reactions: CommentReaction[]
  comment_upvotes: CommentUpvote[]
  layer_id: string | null
  id: number
  created_at: string
  creator: CommentUser
  body: string
  layer?: Pick<Layer, 'id' | 'name' | 'color'> & { is_main: boolean }
}

export enum NotificationType {
  All = 'All',
  AccessGrant = 'AccessGrant',
  AccessRequest = 'AccessRequest',
  Approval = 'Review',
  GroupMembership = 'ChannelMembership',
  Comment = 'Comment',
  OwnershipTransfer = 'OwnershipTransfer',
  ReadReceiptRequest = 'ReadReceiptRequest',
  Suggestion = 'Suggestion',
  Todo = 'Todo',
  InformalFeedbackRequest = 'InformalFeedbackRequest',
}

export enum NotificationSubjectType {
  AccessRequest = 'AccessRequest',
  AccessGrant = 'AccessGrant',
  Access = 'Access',
  ApprovalRequested = 'InboxItemEvents::Reviews::Requested',
  ApprovalRequestedAgain = 'InboxItemEvents::Reviews::RequestedAgain',
  ApprovalReminded = 'InboxItemEvents::Reviews::ReminderSent',
  ApprovalRejected = 'InboxItemEvents::Reviews::Rejected',
  ApprovalApproved = 'InboxItemEvents::Reviews::Approved',
  ApprovalCancelled = 'InboxItemEvents::Reviews::Cancelled',
  Comment = 'Comment',
  CommentResolve = 'Event::CommentResolve',
  CommentUnresolve = 'Event::CommentUnresolve',
  CommentDelete = 'Event::CommentDelete',
  GroupMembership = 'ChannelMembership',
  InformalFeedbackReviewsCreated = 'Event::ArticleInformalFeedbackReviewsCreated',
  InformalFeedbackViewersCreated = 'Event::ArticleInformalFeedbackViewersCreated',
  InformalFeedbackViewersCoCreated = 'Event::ArticleInformalFeedbackViewersCoCreated',
  InformalFeedbackReviewSubmitted = 'Event::ArticleInformalFeedbackReviewSubmitted',
  InformalFeedbackReviewDismissed = 'Event::ArticleInformalFeedbackReviewDismissed',
  InformalFeedbackRequestClosed = 'Event::ArticleInformalFeedbackRequestClosed',
  DesignInformalFeedbackReviewsCreated = 'Event::DesignInformalFeedbackReviewsCreated',
  DesignInformalFeedbackReviewSubmitted = 'Event::DesignInformalFeedbackReviewSubmitted',
  DesignInformalFeedbackReviewDismissed = 'Event::DesignInformalFeedbackReviewDismissed',
  DesignInformalFeedbackRequestClosed = 'Event::DesignInformalFeedbackRequestClosed',
  OwnershipTransfer = 'OwnershipTransfer',
  ReadReceiptRequest = 'ReadReceiptRequest',
  ReadReceiptMarkedRead = 'InboxItemEvents::ReadReceiptRequests::MarkedAsRead',
  ReadReceiptReminderSent = 'InboxItemEvents::ReadReceiptRequests::ReminderSent',
  ReadReceiptClosed = 'InboxItemEvents::ReadReceiptRequests::Closed',
  SuggestionReviewCreated = 'Event::ArticleSuggestionReviewsCreated',
  SuggestionReviewReminder = 'Event::ArticleSuggestionReminder',
  SuggestionReviewRejected = 'Event::ArticleSuggestionReviewRejected',
  SuggestionReviewApproved = 'Event::ArticleSuggestionApprovedAndMerged',
  SuggestionReviewCancelled = 'Event::ArticleSuggestionCancelled',
  Todo = 'Todo',
  TodoUpdated = 'InboxItemEvents::Todos::Updated',
  TodoAssigneeUpdated = 'InboxItemEvents::Todos::AssigneeUpdated',
  TodoCommentCreated = 'Comment',
  TodoCompleted = 'InboxItemEvents::Todos::Completed',
  TodoDeleted = 'InboxItemEvents::Todos::Deleted',
  TodoDueDateUpdated = 'InboxItemEvents::Todos::DueDateUpdated',
  TodoUncompleted = 'InboxItemEvents::Todos::Uncompleted',
}

export interface SuperAdminMode {
  superAdminMode?: number
}

export interface MiniAuth extends UserId, SuperAdminMode {}

export interface Auth extends User, SuperAdminMode {
  signed_up: boolean

  // Last workspace the user was in.
  // Used to determine which workspace the user needs to be redirected to
  // when landing on Almanac.
  last_workspace_id: number | null

  // Last workspace different from core.
  // Used to determine to which workspace the user has to go when getting out of core.
  previous_workspace_id: number | null

  impersonatedBy?: AuthWithHeaders
  redirect_after_signup: string
  replacement_type?: 'unknown' | 'knowledge_base' | 'collaborative_editor' | 'knowledge_base_and_collaborative'
  skip_payment?: boolean
  created_at: string
}

export type StatusValue = 'published' | 'deleted' | 'merged'

export interface UnmergedBranch {
  id: number
  title: string
  master_branch_id: number
}

export interface Doc {
  id: string
  created_at: string
  updated_at: string
  slug: string
  channel?: ChannelType | null
  channel_emoji?: ChannelType | null
  title: string
  header_emoji: string | null
  article_link: string
  external_file_icon?: string | null
  likes_count: number
  copies_count: number
  category_name: string
  company_name: string
  creator_name: string
  is_stub: boolean
  last_edited_at: string | null
  last_edited_by: string | null
  last_viewed_at: string | null
  latest_event_id: string | null
  access: Permission | null
  has_edit_access?: boolean | null
  collection: boolean
  workspace_id: number
  project_id: number | null
  non_workspace_owner?: boolean
  user?: {
    id: number
    avatar_url?: string
    display_name: string
  } | null
  section_id: number | null
  section_position: number | null
  merged_at: string | null
  pending_review?: number | null
  company_id: number
  linked_copy_label: string | null
  workflow_property_value?: PropertyValue | null
}

// TODO: Investigate if it's possible to move File from ArticleService into here and use that for Pick.
export type MinimalFolder = Pick<Doc, 'id' | 'has_edit_access'>

export interface AncestorProject {
  ancestor_projects: AncestorProject[]
  default_project?: boolean
  has_edit_access?: boolean
  id: string
  project_id?: number
  handbook?: Pick<HandbookV2, 'seo_string' | 'key'>
  title: string
  header_emoji?: string | null
  handbook_mode?: boolean
  root_handbook_seo_string?: string | null
  database_mode?: boolean
  default_view?: ViewType
}

export interface PinnedAlmanacFile extends AlmanacFile {
  position: number // workspace pin position
}

export interface AlmanacFile {
  accessor_info: string
  accessor_names: string[]
  ancestor_projects: AncestorProject[]
  brand_plan_schedule?: {
    id: number
    start_date: string
  }
  default_project?: boolean
  uploads_project?: boolean
  external_link?: string
  external_file_icon?: string | null
  external_integration_id?: number
  full_url?: string
  root_handbook_seo_string?: string | null
  root_handbook_key?: string | null
  branches?: AlmanacFile[]
  children?: AlmanacFile[]
  created_by_me?: string
  channel?: ChannelType | null
  channel_emoji?: ChannelType | null
  has_edit_access?: boolean
  has_descendant_articles?: boolean
  has_subprojects?: boolean
  header_emoji?: string | null
  header_emoji_color?: string | null
  id: string
  key: string // both key and id should be the same
  master_branch?: boolean
  shared_at?: string
  created_at?: string
  owner: string
  parent_id?: string
  parent?: {
    id: string
    seo_string: string | null
  }
  pinned?: boolean
  project_id?: number
  slug?: string
  title: string
  todos_count?: number
  type: FileType
  updated_at: string
  workspace_id: string
  real_workspace_id: number
  last_viewed?: string
  last_updated?: string
  main_layer_id?: string
  url?: string
  handbook?: Pick<HandbookV2, 'seo_string' | 'custom_path' | 'key' | 'discarded_at'>
  default_view?: ViewType
  mention_id?: number
  status_kanban_view_id?: number
  ancestor_keys?: string[] // undefined most of the time
  handbook_mode?: boolean
  database_mode?: boolean
  version_control_mode: VersionControlMode
  views_count?: number
  is_template?: boolean
  linked_copy_count?: number
  is_root: boolean
  mime_type?: string
  media_file_id?: number
  design_id?: number
  preview_url?: string
  scheduled_posts?: ScheduledPost[]
  children_preview_urls?: string[]
  is_blank?: boolean
  source_url?: string
  scheduled_post?: ScheduledPostShow
  scheduled_post_id?: number
  variants?: Array<MediaVariant | UnavailableMediaVariant>
  width?: number
  height?: number
  duration?: number
  size?: number
  frame_rate?: number
  aspect_ratio?: number
  thumbnail_url?: string
  template_binder_id?: number
  version?: number
  last_rendered_version?: number
  text_preview?: string
  pending_ai_suggestion_request?: Partial<SuggestionRequest>
  completed_ai_suggestion_request?: Partial<SuggestionRequest>
  failed_ai_suggestion_request?: Partial<SuggestionRequest>
  cross_post_group_id?: number | null
  provider?: string
  provider_data?: MediaFileProviderData
  view_metadata: {
    view: ViewType
  }
  last_used_at?: string
  reviewed_at?: string | null
}

export interface GettyImagesProviderData {
  title?: string
  artist?: string
  collection_name?: string
}

export interface FreepikProviderData {
  title?: string
  author_name?: string
}

export interface PexelsProviderData {
  user_name?: string
}

export type MediaFileProviderData = GettyImagesProviderData | FreepikProviderData | PexelsProviderData | null

export interface MediaVariant {
  transformations: MediaTransformations
  source_url: string
  mime_type: string
  width: number
  height: number
  digest: string
}

export interface UnavailableMediaVariant extends Omit<MediaVariant, 'source_url' | 'mime_type'> {
  source_url: null
  mime_type: null
  width: 0
  height: 0
}

export interface BackgroundRemovalTransformation {
  crop?: boolean
}

export interface GreyScaleTransformation {
  brightness: number
}

export interface MediaTransformations {
  remove_background?: BackgroundRemovalTransformation
  grey_scale?: GreyScaleTransformation
}

export function isAlmanacFile(
  fileOrAncestorProject: AlmanacFile | AncestorProject | null | undefined,
): fileOrAncestorProject is AlmanacFile {
  return fileOrAncestorProject != null && fileOrAncestorProject.project_id != null
}

export interface RecentAlmanacFile extends AlmanacFile {
  last_opened_at: string
  user_owner?: {
    avatar_url: string
    display_name: string
  }
  user: {
    avatar_url: string
    display_name: string
  }
  open_informal_feedback_request?: {
    requester: string
    type: string
  }
  workflow_status_property?: {
    value: string
    meta: {
      color: string
      description?: string
    }
  }
}

export type MinimalFile = Pick<
  AlmanacFile,
  'type' | 'title' | 'key' | 'id' | 'parent_id' | 'has_edit_access' | 'url' | 'full_url'
>

export interface ArticleNoElements {
  id: string
  created_at: string
  updated_at: string
  article_link: string
  category_id: number | null
  category_ids: number[]
  category_name: string | null
  company: WorkspaceSettings
  company_name: string | null
  copies_count: number
  cover_image_url: string | null
  cover_image_position: number | null
  creator_id: number
  current_user_access: 'edit' | 'comment' | 'read' | null
  header_emoji: string | null
  hide_paywall: boolean
  is_default_cover_image: boolean
  is_stub: boolean
  likes_count: number
  made_public: boolean
  made_public_at: string | null
  meta_description: string | null
  meta_image_url: string | null
  non_workspace_owner?: boolean
  our_research_process: string
  our_research_process_title: string | null
  project: {
    emoji: string | null
    id: number
    key: string
    title: string
    seo_string: string | null
  } | null
  show_on_trending: boolean
  title: string
  version_name?: string
  dismissed_living_modal: boolean
  workspace_id?: number
  company_id?: number
  branch_count: number
  password?: string | null
  shared_to_web: 'read' | 'edit' | null
  tracked_at: string | null
  track_from_version: number | null
}

export interface ArticleStyleProperty {
  background_color?: string | null
  color?: string
  font_family?: FontFamilyType
  font_size?: string
  font_style?: string
  font_weight?: string
  line_height?: number
  padding_bottom?: string
  padding_top?: string
  text_align?: string
  text_decoration?: string
  text_transform?: string
}

export type Position = {
  top: number
  left: number
}

export interface ArticleStyle {
  blockquote: ArticleStyleProperty
  body: ArticleStyleProperty
  callout: ArticleStyleProperty
  h1: ArticleStyleProperty
  h2: ArticleStyleProperty
  h3: ArticleStyleProperty
  h4: ArticleStyleProperty
}

export interface LayerSummary {
  id: string
  name: string
  is_main: boolean
  article_id: string
}

export interface LayerPermissions {
  can_read: boolean
  can_comment: boolean
  can_edit: boolean
  can_merge: boolean
  can_edit_content: boolean
  can_merge_partial: boolean
  highest: Permission
}

export enum LayerStatus {
  DRAFT = 'draft',
  IN_REVIEW = 'in_review',
  REVIEWED = 'reviewed',
  APPROVED = 'approved',
  SUGGESTIONS = 'suggestions',
}

export interface Layer extends LayerSummary {
  article_id: string
  elements: any[]
  last_snapshot_delta_steps?: { data: any }[]
  last_step_version: number
  parent_layer: Layer | null
  color: string | null
  tracked_changes_count: number
  public: boolean
  sharing_mode: LayerSharingMode
  is_main: boolean
  owner: {
    id: number
  }
  protected: boolean
  permissions: LayerPermissions
  status: LayerStatus
  current_version: number
  completed_ai_suggestion_request: CompletedSuggestionRequest | null
  pending_ai_suggestion_request: SuggestionRequest | null
}

export enum LayerVisibilityLevel {
  Private = 'private',
  Public = 'public',
}

export enum LayerSharingMode {
  Private = 'private',
  Public = 'public',
  Shared = 'shared',
}

export type LayerAccessGrant = UserLayerAccessGrant | GroupLayerAccessGrant

export enum LayerAccessor {
  User = 'user',
  Group = 'group',
}

export type Access = Permission | 'owner'

interface BaseLayerAccessGrant {
  id: number
  access: Access
}
export interface UserLayerAccessGrant extends BaseLayerAccessGrant {
  type: LayerAccessor.User
  user: {
    id: number
    avatar_url: string | null
    email: string
    display_name: string | null
  }
}

export interface GroupLayerAccessGrant extends BaseLayerAccessGrant {
  type: LayerAccessor.Group
  group: {
    id: number
    name: string
    members_count: number
    avatar_url: string | null
  }
}

export interface TrackedChange {
  id: string
  layer_id: string
  comments: TrackedChangeComment[]
  reactions: TrackedChangeReaction[]
}

export interface TrackedChangeComment {
  id: string
  tracked_change_id: string
  user: CommentUser
  body: string
  html_body: string | null
  created_at: string
  reactions: TrackedChangeCommentReaction[]
}

export interface TrackedChangeReaction {
  id: number
  reaction: string
  user_id: number
  user_name: string
  tracked_change_id: string
}

export interface TrackedChangeCommentReaction {
  id: number
  reaction: string
  user_id: number
  user_name: string
  tracked_change_comment_id: string
}

export interface Transcription {
  id: number
  content: string | null
  created_at: string
  updated_at: string
  status: 'pending' | 'successful' | 'failed' | 'started' | 'aborted'
  source_url: string
  language: string
  failure_reason?: string
}

export type MasterBranch = {
  id: string
  url: string
  slug: string
  title: string
  header_emoji: string | null
  non_workspace_owner?: boolean
  creator_name: string
  updated_at: string
  current_user_permission: Permission
  has_edit_access: boolean
}

export interface AlmanacFilePermissions {
  can_transition_to: State['key'][]
  can_read: boolean
  can_comment?: boolean
  can_edit: boolean
  can_edit_content: boolean
  is_owner?: boolean
  access?: Permission
}

export interface VcmMetadata {
  author: {
    id: number
    display_name: string
  }
}

export interface VersionControlMode {
  value: boolean
  metadata: VcmMetadata | null
}

export enum DesignActionType {
  Template = 'template',
  CoreImage = 'core_image',
  BackgroundImage = 'background_image',
  AddText = 'add_text',
  GenerateCaption = 'generate_caption',
  ApplyBrand = 'apply_brand',
  SchedulePost = 'schedule_post',
  Download = 'download',
}

export type CompletedDesignActions = Partial<Record<DesignActionType, boolean>>

export type DesignAspectRatio = {
  /** Width of the design */
  width: number
  /** Height of the design */
  height: number
  /** Name of the aspect ratio (ie. Instagram post)*/
  name: string
  /** Icon of the aspect ratio or the target platform */
  icon: Icon
  /** Image of the aspect ratio or the target platform */
  image: string
}

export interface DesignSeed {
  background?: BackgroundAssetProps
  apply_brand?: boolean
}

export type AlmanacFileAncestor = Omit<AlmanacFileSummary, 'ancestors'>

export interface AlmanacFileSummary {
  id: string
  title: string
  emoji: string | null
  permissions: AlmanacFilePermissions
  pinned?: boolean
  version_control_mode: VersionControlMode
  workspace: {
    id: number
    key: string
    version_control_mode: VersionControlMode
  }
  is_version_controlled: boolean
  ancestors: AlmanacFileAncestor[]
  parent?: {
    id: string
    seo_string: string | null
  }
  seo_string: string
  is_template?: boolean
  is_root: boolean
  database_mode?: boolean
  default_view?: ViewType
  channel?: ChannelType | DesignChannelType | null
  channel_emoji?: ChannelType | DesignChannelType | null
  external_file_icon?: string | null
  content?: any
  pending_ai_suggestion_request?: Partial<SuggestionRequest>
  completed_ai_suggestion_request?: SuggestionRequest
  design_id?: number
  source_visual_element_id?: number
  captions?: string[]
  selected_caption_id?: number
  brand_visual_kit_id?: number
  scheduled_post_id?: number
  asset_type: AssetType
  asset_id: number
  width?: number
  height?: number
  duration?: number
  completed_actions?: CompletedDesignActions
  seed?: DesignSeed
  uploads_project?: boolean
  url: string
  type: FileType
  mime_type?: string
  size?: number
  source_url?: string
  is_blank?: boolean
  preview_url?: string
  post_at?: string
  children_preview_urls?: string[]
  thumbnail_url?: string
  template_binder_id?: number
  visual_element?: VisualElement | null
  version?: number
  last_rendered_version?: number
  blank_toast_dismissed?: boolean
  created_at: string
  cross_post_group_id?: number | null
  related_ad_id?: number | null
  is_related_ad_published?: boolean
  shared_to_web?: 'read' | 'edit' | null
}

export interface ArticleFlat extends ArticleNoElements {
  access_map?: ArticleAccessMap
  ancestor_projects?: AncestorProject[]
  article_style?: ArticleStyle
  channel?: ChannelType
  channel_emoji?: ChannelType | null
  blog_redirect_path?: string | null
  changed_content: boolean
  company_logo_url?: string | null
  external_link?: string
  external_file_icon?: string | null
  creator_image_url: string | null
  creator_name: string
  category_ids: number[]
  creator_company: string | null
  creator_role: string | null
  current_version: number
  custom_handbook_path?: string
  direct_access_level: Permission | null
  elements: any[]
  extra_steps?: { data: any }[]
  full_url?: string
  root_handbook_seo_string?: string | null
  root_handbook_key?: string | null
  header_emoji_color: string | null
  in_handbook?: boolean
  public_handbook_image_url?: string
  how_to_use_this: string[] | null
  initial_article: boolean
  last_edited_at?: string
  last_edited_by: string | null
  last_step_version: number
  locked: boolean
  main_layer_id?: string
  is_main_layer_owner?: boolean
  master_branch: MasterBranch | null
  merged_at: string | null
  merged_by: UserCard | null
  nofollow: boolean
  noindex: boolean
  project_id: number | null
  project_key: string | null
  article_reaction?: ArticleReaction | null
  schema_version: number
  show_in_read_mode: boolean
  slug: string
  tracked_version: boolean
  track_from_version: number | null
  tracked_at: 'string' | null
  tracked_by: User | null
  why_trust_this: string | null
  workflow_status?: WorkflowStatus | null
  workflow_property_value?: PropertyValue | null
  workspace_article_style?: ArticleStyle
  handbook_published_at?: string | null
  guest_inviter_display_name?: string | null
  new_handbook?: HandbookV2
  main_layer: Layer
  linked_copies_count: number
  linked_copy_label: string | null
  company_id: number
  is_crdt: boolean
  user: {
    id: number
    display_name: string
    avatar_url: string | null
  }
  almanac_file: Omit<AlmanacFileSummary, 'ancestors'>
  layout: 'fixed' | 'fluid'
  scheduled_post?: ScheduledPostShow
}

export interface ArticleAccessMap {
  has_edit_access: boolean
  edit_access_as_almanac_admin: boolean
  edit_access_through_sharing_link: boolean
  edit_access_as_super_admin: boolean
  has_comment_access: boolean
  has_comment_access_through_sharing_link: boolean
}

export interface TimestampedArticle extends DbObject {
  elements: any[]
}

export interface PaginationMeta {
  first_page: string | null
  last_page: string | null
  next_page: string | null
  prev_page: string | null
  total_count: number
  total_pages: number
}

export interface PaginationParams {
  page?: number
  items?: number
}

export interface ImportedArticle {
  id: number
  card_count: number
  title: string
}

export type DocRightRailViewItem =
  | 'Comments'
  | 'Tasks'
  | 'Information'
  | 'InsertMenu'
  | 'Activity'
  | 'TableOfContents'
  | 'TrackedChanges'
  | 'LayerChanges'
  | 'Timer'
  | 'Format'
  | 'AIAssistant'
  | 'PlagiarismDetection'
  | 'SEOGrading'
  | 'Metadata'

export interface DocRightRailSection {
  key: string
  label: string
  collapsed: boolean
  type: CollabItemType
  items: (type: CollabItemType) => CollabItem[]
  showOverride: () => boolean
}

export enum BreadcrumbsContextType {
  Article,
  Links,
  Page,
  Group,
  AlmanacFile,
}

export type AssetType = 'Article' | 'Project' | 'MediaFile' | 'Design'

export type AlmanacFileBreadcrumbsContext = {
  type: BreadcrumbsContextType.AlmanacFile
  id: string
  ancestors: AlmanacFileSummary['ancestors']
  assetType: AssetType
}

export type ArticleMeta = Pick<
  ArticleFlat,
  | 'article_link'
  | 'article_style'
  | 'category_ids'
  | 'changed_content'
  | 'channel'
  | 'channel_emoji'
  | 'company'
  | 'company_id'
  | 'company_logo_url'
  | 'company_name'
  | 'cover_image_position'
  | 'cover_image_url'
  | 'created_at'
  | 'creator_id'
  | 'creator_name'
  | 'current_user_access'
  | 'current_version'
  | 'custom_handbook_path'
  | 'direct_access_level'
  | 'external_link'
  | 'external_file_icon'
  | 'handbook_published_at'
  | 'header_emoji'
  | 'header_emoji_color'
  | 'id'
  | 'in_handbook'
  | 'is_default_cover_image'
  | 'is_stub'
  | 'linked_copy_label'
  | 'linked_copies_count'
  | 'locked'
  | 'made_public'
  | 'made_public_at'
  | 'master_branch'
  | 'merged_at'
  | 'non_workspace_owner'
  | 'project'
  | 'project_key'
  | 'public_handbook_image_url'
  | 'scheduled_post'
  | 'schema_version'
  | 'show_on_trending'
  | 'slug'
  | 'title'
  | 'track_from_version'
  | 'tracked_at'
  | 'updated_at'
  | 'workflow_property_value'
  | 'workflow_status'
  | 'workspace_article_style'
  | 'workspace_id'
  | 'main_layer'
  | 'almanac_file'
  | 'layout'
>

export enum SidebarTabs {
  Organizational,
  Handbooks,
}

export type CardDismissal =
  | 'handbook_editor_footer'
  | 'search_filter_button_tooltip'
  | 'workspace_selector_filter_button_tooltip'
  | 'onboarding'
  | 'blaze_onboarding'
  | 'blaze_onboarding_checklist'
  | 'blaze_redesign_notification'
  | 'kanban_update_notification'
  | 'scheduled_posts_v2_notification'
  | 'home_invite_cta'
  | 'home_generate_content_cta'
  | 'home_turn_into_cta'
  | 'home_referral_cta'
  | 'home_header_referral_cta'
  | 'left_rail_referral_cta'
  | 'connect_account_cta'
  | 'schedule_post_cta'
  | 'content_preferences_cta'
  | 'review_first_campaign'
  | 'ai_generated_notification'
  | 'campaigns_cta'
  | 'visual_styles_existing_users_modal'
  | 'visual_styles_existing_users_banner'

export type WizardTourDismissal =
  | 'abandoned_cart: '
  | 'about_us'
  | 'amazon_ads'
  | 'battle_card'
  | 'billing_and_payments'
  | 'bing_ads'
  | 'blog_ideas'
  | 'blog_post'
  | 'call_script'
  | 'candidate_nurture_sequence'
  | 'care_instructions'
  | 'case_study'
  | 'cold_outbound_email_sequence'
  | 'didnt_sign_up'
  | 'facebook_ads'
  | 'facebook_post'
  | 'faqs'
  | 'google_ads'
  | 'help_docs'
  | 'impact'
  | 'instagram_posts'
  | 'instagram_captions'
  | 'instagram_reel_script'
  | 'job_description'
  | 'landing_page'
  | 'linkedin_ads'
  | 'linkedin_posts'
  | 'lost_customer'
  | 'mission'
  | 'newsletter'
  | 'newsletter_ideas'
  | 'nurture_email_sequence'
  | 'one_pager'
  | 'payment_confirmation'
  | 'pinterest_ads'
  | 'pre_call_checklist'
  | 'press_release'
  | 'pricing'
  | 'product_demo'
  | 'product_launch_amazon'
  | 'product_launch_etsy'
  | 'product_launch_google'
  | 'product_launch_shopify'
  | 'product_launch_website'
  | 'promotional_email_sequence'
  | 'reply_templates'
  | 'research'
  | 'return_policy'
  | 'shipping_policy'
  | 'sms_messages'
  | 'testimonial'
  | 'tiktok_ads'
  | 'tiktok_script'
  | 'tweets'
  | 'twitter_thread'
  | 'warm_outbound_email_sequence'
  | 'welcome_series'
  | 'youtube_ads'
  | 'youtube_script'
  | 'instagram_post'
  | 'instagram_story'
  | 'instagram_reel'
  | 'tiktok_post'
  | 'tiktok_thumbnail'
  | 'youtube_profile_picture'
  | 'youtube_channel_art'
  | 'facebook_cover'
  | 'facebook_profile_picture'
  | 'facebook_post'
  | 'facebook_marketplace'
  | 'linkedin_banner'
  | 'linkedin_profile_picture'
  | 'linkedin_post'
  | 'twitter_profile_picture'
  | 'twitter_cover_image'
  | 'whatsapp_sticker'
  | 'pinterest'
  | 'ebay'
  | 'poshmark'
  | 'depop'
  | 'mercari'
  | 'mercadolibre'
  | 'shopee'
  | 'lazada'
  | 'etsy'
  | 'vinted'
  | 'amazon'
  | 'shopify'
  | 'twitter_post'
  | 'outline'
  | 'brainstorm'
  | 'content_brief'
  | 'campaign'
  | 'seo_planning'
  | 'instagram_planning'
  | 'twitter_planning'
  | 'facebook_planning'
  | 'linkedin_planning'
  | 'content_plan'

export type NotificationChannel = 'inbox' | 'email' | 'slack'
export type NotificationSettingsItem = {
  [key in NotificationChannel]?: boolean
}

export type NotificationGroup =
  | 'documentAccess'
  | 'comments'
  | 'tasks'
  | 'reminders'
  | 'reviews'
  | 'scheduledPostsPublished'
  | 'scheduledPostsFailedToPublish'

export type NotificationSettings = {
  [key in NotificationGroup]?: NotificationSettingsItem
}

export type CalendarPreferences = {
  calendarDayGridType: DayGridType
  scheduledTimeSortDirection?: SortingDirection
}
export interface UserAppPreferences {
  leftNavLocked?: boolean
  activeLeftNavTab?: SidebarTabs
  cardDismissals?: CardDismissal[]
  wizardTourDismissal?: WizardTourDismissal[]
  focusModeDisabled?: boolean
  notifications?: NotificationSettings
  notificationsMutedAt?: string | null
  hideCommandLineTips?: boolean
  selectedLanguage?: string
  scheduledPostsView?: 'calendar' | 'list'
  calendarPreferences?: CalendarPreferences
}

export type LinksBreadcrumbsContext = {
  links: { key: string | number; title: string; to: string }[]
  type: BreadcrumbsContextType.Links
}

export type PageBreadcrumbsContext = {
  page: { title: string | ReactNode }
  type: BreadcrumbsContextType.Page
}

export type GroupBreadcrumbsContext = {
  group: ChannelInfo
  workspaceId: string | number
  onEmojiUpdate: (emoji: string | null) => void
  type: BreadcrumbsContextType.Group
}

export type BreadcrumbsContext =
  | LinksBreadcrumbsContext
  | PageBreadcrumbsContext
  | GroupBreadcrumbsContext
  | AlmanacFileBreadcrumbsContext
  | null

export type WorkflowStatus = {
  text: string
  state: WorkflowState
}

export type WorkflowState = 'read_receipt' | 'informal_feedback_request' | 'approval' | 'suggest_changes'

export enum WorkflowType {
  ReadReceipt = 'read_receipt',
  FeedbackRequest = 'informal_feedback_request',
  ApprovalRequest = 'approval',
  SuggestChanges = 'suggest_changes',
}

export interface HandbookIndexArticle {
  id: string
  custom_handbook_path?: string | null
  slug: string
  editor?: boolean
  handbook_published_at?: string | null
  header_emoji_color: string | null
  title: string
  article_link: string
  public_handbook_image_url?: string
  key?: string
  url?: string
  v2?: boolean
}

export interface Handbook {
  indexArticleId: string
}

export enum HandbookType {
  Handbook = 'handbook',
  Folder = 'folder',
}
export interface HandbookV2 {
  id: number
  key: string
  workspace_id: number
  workspace?: {
    name: string
    logo_url?: string
    id?: number
  }
  cover_doc?: ArticleFlat
  items: HandbookItem[]
  name: string
  emoji?: string | null
  seo_string: string
  custom_path?: string | null
  image_url?: string | null
  published_at: string | null
  discarded_at?: string | null
  publisher_display_name: string | null
  current_user_access: Permissions
  view_mode: HandbookViewMode
  cascading_share: boolean
  folder?: Project | null
  theme_color?: HandbookTheme | null
  theme_font?: FontFamilyType | null
  type: HandbookType
  parent_handbook?: {
    url: string
    name: string
  }
  redirect_to_migrated?: string
  shared_to_web?: boolean
  sort_type?: HandbookSortOptions
  theme: ITheme
  ancestors_theme: ITheme
  pending_ai_batch_requests: BatchSuggestionRequestSummary[]
  brand_plan_schedule?: BrandPlanSchedule
}

export enum HandbookViewMode {
  Read = 'read',
  Comment = 'comment',
  Edit = 'edit',
}

export enum HandbookItemType {
  Section = 'section',
  Document = 'doc',
  Link = 'link',
  NestedHandbook = 'nested_handbook',
}

export interface HandbookItemDocument {
  key: string
  seo_string: string
  channel_emoji?: ChannelType | null
  header_emoji: string | null
  external_file_icon?: string | null
  linked_copy_count?: number
  almanac_file?: {
    is_template?: boolean
  }
}

export interface HandbookItemFolder {
  id: number
  key: string
  header_emoji: string | null
  url?: string
  default_view: ViewType
  almanac_file: AlmanacFileSummary
}

export interface HandbookItem {
  id: number
  handbook_id: number // Used to reposition the item when doing DnD within nested handbooks
  type: HandbookItemType
  position: number
  parent_id: number | null
  title?: string
  url?: string
  favicon?: string
  document?: HandbookItemDocument
  folder?: HandbookItemFolder
  nested_handbook?: {
    id: number
    url: string
    key: string
    roots_count: number
  }
  mention_id?: number
  created_at?: number
  handbook_mode?: boolean
  database_mode?: boolean
  default_view?: ViewType
  handbook: {
    key: string
    id: number
  }
}

export interface DocHandbookItem extends HandbookItem {
  type: HandbookItemType.Document
  document: HandbookItemDocument
  title: string
}

export interface SectionHandbookItem extends HandbookItem {
  type: HandbookItemType.Section
}

export interface LinkHandbookItem extends HandbookItem {
  type: HandbookItemType.Link
}

export interface NestedHandbookHandbookItem extends HandbookItem {
  type: HandbookItemType.NestedHandbook
  folder: HandbookItemFolder
}

export type GenericHandbookItem = DocHandbookItem | SectionHandbookItem | LinkHandbookItem | NestedHandbookHandbookItem

export enum HandbookSortOptions {
  Custom = 'custom',
  Newest = 'newest',
  Oldest = 'oldest',
  NameAscending = 'name_asc',
  NameDescending = 'name_desc',
}

export interface UnregisteredUserState {
  user?: Auth
  headers?: { authorization: string }
  status: FetchRequestState
  bannerDismissed?: boolean
}

export interface FileAssignedProperty {
  loadingState: FetchRequestState
  id?: number
  values?: PropertyValue[]
}

export interface FileAssignedProperties {
  [propertyId: number]: FileAssignedProperty
}

export interface AuthHeaders {
  authorization?: string
  'admin-authorization'?: string
}

export interface AuthWithHeaders extends Auth {
  headers: AuthHeaders
}

export interface RootState {
  anonymousUser: AnonymousUser | null
  articleMeta: KeyValue<ArticleMeta | undefined>
  articlePassword: KeyValue<string>
  articleInformalFeedbackRequestsFetchRequests: {
    // article Key
    [key: string]: FetchRequestState
  }
  auth: Auth | null
  headers: AuthHeaders
  breadcrumbsContext: BreadcrumbsContext
  cardDismissals: CardDismissal[]
  wizardTourDismissals: WizardTourDismissal[]
  channelInviteModalVisible: boolean
  commandLineVisible: boolean
  comments: {
    activeId: number | undefined
    focus: boolean
    commentPromptText: string
    isDeletingOrResolving: boolean
    highlightedId: number | undefined
    newCommentActive: boolean
    records: { [id: number]: Comment }
    hiddenLayersIds: string[]
  }
  createFromTemplateModalVisible: boolean
  onboardingLoaderVisible: boolean
  currentArticleId: string | null
  documentSettingsModalVisible: boolean
  educationPopovers: EducationPopover[]
  favorites: Favorite[]
  filesProperties: {
    [fileId: string]: FileAssignedProperties
  }
  floatingSidebar: boolean
  showFocusMode: boolean
  categories: {
    records: CategoryDetail[] | null
  }
  focusMode: {
    manual: boolean
    showFocusMode: boolean
    focusModeDisabled: boolean
  }
  followees: { [id: number]: Actions.FolloweeAdd | undefined }
  forceFinderRefresh: boolean
  getter: { [key: string]: Response | undefined }
  globalPaywallBannerDismissed: boolean
  handbook: HandbookV2 | null
  inboxThreads: {
    isLoading: boolean
    unreadTodosCount: number
    unreadReviewsCount: number
  }
  informalFeedbackRequests: {
    [id: number]: InformalFeedbackRequest
  }
  moveModalVisible: boolean
  navigation: {
    everyoneDocIds: string[]
    hideLeftNavRail: boolean
    preferredSort: NavSortOrder
    privateDocIds: string[]
    openWorkspaceDropdown: boolean
  }
  openFolderIds: { [key: string]: any }
  portal: { portalData?: Portal; isEditMode?: boolean; loaded: boolean }
  properties: {
    [id: number]: Property
  }
  highlightedPropNames: HighlightPropertiesByNameProps
  articleReactions: { [articleId: string]: ArticleReaction }
  readReceipts: { [articleId: string]: ReadReceiptRequestsAndTasks & { activeTasksCount: number } }
  requestedArticles: { [id: string]: RequestedArticle | undefined }
  reviews: {
    [id: number]: InformalFeedbackRequest
  }
  reviewsFetchRequests: FetchRequestState
  searchBarActive: boolean
  shareWithTeamModalVisible: boolean
  sidebar: { locked: boolean; activeTab: SidebarTabs }
  sidebarWidth: number
  smartLinkArticlesHelper: { articles: MentionableArticle[] }
  smartLinks: { [id: string]: string }
  snippets: { show: boolean; snippets: Snippet[] }
  suggestChangesModal: {
    state: null | 'suggesting' | 'submitted' | 'merged'
    mergedUrl?: string
    mainName?: string
  }
  suggestions: {
    [id: number]: Suggestion
  }
  todos: {
    activeId?: number
    highlightedId?: number
    records: {
      [id: number]: Todo
    }
  }
  timeline: Timeline | null
  trackChanges: { enabled: boolean; showChanges: boolean }
  unregisteredUser: UnregisteredUserState
  userArticleLikes: { id: string }[]
  versionModalVisible: boolean
  websocket: { connection: WebsocketConsumer }
  workspace: {
    workspaces: Workspace[]
    currentWorkspaceId: number
  }
  colors: UserColorByArticle
  deleteModalVisible: boolean
}

export interface UserColorByArticle {
  [articleId: string]: UserColors
}

export interface UserColors {
  [userId: string]: string
}

export interface UsersMetadata {
  [userId: string]: {
    onClick?: (event: React.MouseEvent) => void
    color?: string | undefined
    present?: boolean | undefined
  }
}

export enum FetchRequestState {
  Idle = 'idle',
  Loading = 'loading',
  Resolved = 'resolved',
  Rejected = 'rejected',
}

export interface MentionableArticle {
  key: string
  objectID: string
  creator_name: string
  header_emoji: string | null
  external_file_icon: string | null
  channel_emoji?: ChannelType | null
  id: number | string
  subtitle: string | null
  title: string
  last_edited_by: string | null
  // TODO: use only number after reindexing Algolia indexes
  last_edited_at: number | string
  url: string
}

export interface AccessRecord<T> {
  accessor_id: number
  accessor_type: 'Company' | 'User' | 'Channel' | 'Project'
  id: number
  permission: Permissions
  accessor: T
  overwritten_permission_by: {
    type: 'workspace' | 'channel'
    name: string
  } | null
}

export type Permissions = 'read' | 'comment' | 'edit' | 'owner'

export interface ProjectAccess {
  title: string
  color: string
  emoji: string
  member_count: number
  id: number
  workspace_id: number
}

export interface AccessIndex {
  company: AccessCompany
  company_access?: AccessRecord<AccessCompany>
  sharing_link_access?: string
  homepage_doc?: boolean
  channel_accesses: AccessRecord<AccessChannel>[]
  current_user_access?: AccessRecord<AccessUser>
  user_accesses: AccessRecord<AccessUser>[]
  project_access: AccessRecord<ProjectAccess>
}

export interface AccessUser {
  id: number
  display_name: string
  email: string
  first_name: string
  last_name: string | null
  avatar_url: Avatar
}

export interface AccessChannel extends ChannelInfo {}

export interface MinimalChannel {
  name: string
  id: number
  key: string
  members_count: number
}

export interface AccessCompany {
  id: number
  company_logo: string
  logo_url: string
  name: string
  users_count: number
}

export interface CompanyMember {
  id: number
  first_name: string
  last_name: string | null
  display_name: string | null
  email: string
  status: string
  avatar_url: string | null
}

export interface SettingsGuest extends CompanyMember {
  type: 'Guest'
  created_at: Date
  confirmed_at: Date
  last_activity_at: Date
}

export interface MentionCompanyMember extends CompanyMember {
  display: string
}

export interface CompanyUser {
  display_name: string
  email: string
  first_name: string
  id: number
  last_name: string | null
  status: string
}

export interface AlgoliaArticle {
  key: string
  category_ids: number[]
  avatars: Avatar[]
  cards: number
  category: string
  category_name: string
  company: number
  company_name: string
  cover_image_url: string
  contributors: string[]
  contributor_ids: number[]
  contributors_count: number
  creator_id: number
  creator_name: string
  handbook_index?: boolean
  objectID: string
  our_research_process: string
  quality: number
  quality_range: string
  seo_string: string
  social_shares_count: number
  stub: boolean
  title: string
  published_at: number // @deprecated - use made_public_at
  updated_at: number
  viewable_by: number[]
  views: number
  first_card_title: string
  second_card_title: string
  likes_count: number
  copies_count: number
  pinned_on_channels: number[]
  url: string
  header_emoji: string | null
  creator_url: string
  made_public_at: number
  __position: number
  creator_image_url: string | null
  creator_company: string | null
  creator_role: string | null
  last_edited_by: string
  last_edited_at: number | null
  _highlightResult: { plain_text?: { matchedWords: string[]; value: string } }
  plain_text: string
  workflow_status?: WorkflowStatus | null
  header_emoji_color: string | null
  preview_url: string
  file_type: string
  channel: string
  channel_emoji: string
  database_mode: boolean
  default_view: string
  children_preview_urls: string[]
  is_blank: boolean
  uploads_project: boolean
}

export interface AlgoliaSection {
  title: string
  article_id: number
  objectID: string
  uuid: string
  position_in_article: number
  workspace_id: number
  article_title: string
  slug: string
}

export interface AlgoliaCategory extends Category {
  objectID: string
}

export interface Following {
  followee_id: number
  follower_id: number
  id: number
}

export interface Profile {
  almanac_certified: boolean
  avatar_url: Avatar
  bio: string | null
  company_name: string | null
  company_role: string | null
  company_url: string | null
  copies_count_across_all_docs: number
  copies_count_across_public_docs: number
  cover_image_url: string | null
  display_name: string | null
  email: string
  email_visible?: boolean
  first_name: string
  followees_count: number
  followers_count: number
  following_id: number | null
  id: number
  intro: string | null
  job_search: boolean
  joined_date: string
  last_name: string | null
  likes_count_across_all_docs: number
  likes_count_across_public_docs: number
  linkedin: string | null
  location: string | null
  meta_description: string | null
  personal_url: string | null
  personal_url_title: string | null
  requested_articles_count: number
  shares_count_across_all_docs: number
  shares_count_across_public_docs: number // @deprecated - use shares_count_across_public_docs
  signed_up?: boolean
  twitter: string | null
}

export interface UserCard {
  avatar_url: Avatar
  bio: string | null
  company_name: string | null
  company_role: string | null
  email: string
  first_name: string
  last_name: string | null
  first_name_last_initial: string
  display_name: string | null
  cover_image_url: string | null
  signed_up?: boolean
}

export interface EditProfileFormData {
  first_name: string
  last_name: string
  company_role?: string
  intro?: string
  twitter?: string
  linkedin?: string
  location?: string
  company_name?: string
  company_url?: string
  bio?: string
  personal_url?: string
  personal_url_title?: string
  job_search: boolean
  [key: string]: string | boolean | undefined // index signature
}
export interface WorkspaceSamlSettings {
  enabled: boolean
  use_idp_metadata_url: boolean
  idp_metadata_url?: string
  idp_metadata_xml?: string
  email_domain?: string
}

export interface HomepageDoc {
  last_edited_at?: string | null
  last_edited_by: string | null
  slug: string
  title: string
}

export interface WorkspaceSettings extends CompanyDetail {
  article_style?: ArticleStyle
  can_enable_workspace_landing: boolean
  homepage_doc: HomepageDoc | null
  homepage_doc_slug: string | null
  version_lock?: VersionLock
  style: {
    banner_background_color: string
    banner_text: string | null
    banner_text_color: string
    company_id: number
    id: number
    personalized_list: boolean
    show_almanac_side_bar: boolean
    show_banner: boolean
    show_contributor_sidebar: boolean
    show_logo: boolean
    show_make_public: boolean
    show_popular_tags: boolean
    show_portal: boolean
    show_what_we_are_reading: boolean
  }
  saml_settings: WorkspaceSamlSettings | null
  scim_enabled: boolean
  scim_token: string
  workspace_domain: WorkspaceDomain | null
  paying: boolean
  version_control_mode: VersionControlMode
  theme: ITheme
  allow_public_links: boolean
  documentation_enabled: boolean
  layers_enabled: boolean
  color_scheme: WorkspaceColorScheme | null
  app_logo_url: string | null
  is_whitelabel: boolean
  white_label_color: string | null
}

interface BaseSettingsMember {
  id: number
  email: string
  first_name: string
  last_name: string | null
  display_name: string
  avatar_url?: Avatar
  seo_string?: string
  role: 'SuperAdmin' | 'Admin' | 'User'
  created_at: Date
  confirmed_at: Date
  last_activity_at: Date
}

export interface ActiveSettingsMember extends BaseSettingsMember {
  type: 'User'
}

// the `id` of a DeletedMembership is the membership id, not the user id
export interface DeletedMembership extends BaseSettingsMember {
  type: 'DeletedMembership'
  member_id: number
  deleted_at: Date
}

export interface MembershipInvitation extends BaseSettingsMember {
  type: 'Invitation'
  invitation_sender: { id: number; first_name_last_initial: string; display_name: string } | null
  invited_at: Date
}

export type SettingsMember = ActiveSettingsMember | DeletedMembership | MembershipInvitation

export type IsNotDeletedMembership = (member: SettingsMember) => member is ActiveSettingsMember | MembershipInvitation

export interface ShareUser {
  avatar_url: Avatar
  company: AccessCompany
  confirmed: boolean
  display_name: string
  email: string
  first_name: string
  id: number
  last_name: string | null
  seo_string: string
}

export interface MentionShareUser extends ShareUser {
  display: string
}

export interface VersionLock {
  enabled: boolean
  updated_at: string
  user: ShareUser
}

export enum UserRole {
  ADMIN = 'admin',
  SUPER_ADMIN = 'super_admin',
  MEMBER = 'member',
  GUEST = 'guest',
  UNAUTHED = 'unauthed',
}

export const WorkspaceColorSchemes = [
  'blue',
  'green',
  'purple',
  'red',
  'yellow',
  'gray',
  'magenta',
  'teal',
  'light',
] as const

export type WorkspaceColorScheme = (typeof WorkspaceColorSchemes)[number]

export const WorkspaceLightHeaderColorSchemes: WorkspaceColorScheme[] = ['light']

export interface Workspace {
  id: number
  name: string
  company_size_enum?: number
  created_at: string
  handbook_default_read_mode: boolean
  homepage_doc_slug: string | null
  homepage_doc?: HomepageDoc | null // only exists on full company serializer
  avatar_color?: number | null
  logo_url: string
  key: string
  unpaid_articles_count: number
  is_new: boolean
  isFirstView?: boolean
  guest: boolean
  guest_articles_count: number
  admin: boolean
  super_admin?: boolean
  paying: boolean
  invitation_token: string
  invitation_token_enabled: boolean
  invitation_token_expiration_days: number
  role: UserRole
  show_make_public?: boolean
  show_portal: boolean
  show_all_handbooks: boolean
  workspace_domain: WorkspaceDomain | null
  github_integration_enabled?: boolean
  members_count: number
  early_adopter?: boolean
  version_lock?: VersionLock
  payment_failed: boolean
  auto_membership_domains: (string | undefined)[]
  has_old_handbooks?: boolean
  can_migrate_handbooks?: boolean
  features: FlaggedFeature[]
  version_control_mode: VersionControlMode
  theme?: {
    hue?: Hue
    preset?: ThemePreset
    overrides?: Overrides
  }
  seeded?: boolean
  allow_public_links: boolean
  documentation_enabled: boolean
  layers_enabled: boolean
  can_start_free_trial: boolean
  color_scheme: WorkspaceColorScheme | null
  app_logo_url: string | null
  is_whitelabel: boolean
  white_label_color: string | null
  collection_paused?: boolean
  snippets_enabled: boolean
  image_library_project?: {
    id: number
    key: string
  }
  subscription_provider?: string
  is_virtual_marketer: boolean
  is_virtual_marketer_onboarding_completed: boolean
  show_virtual_marketer_upgrade: boolean
  pause_autopilot_generate_at: string | null | undefined
  pause_autopilot_publish_at: string | null | undefined
  resume_autopilot_generate_at: string | null | undefined
  resume_autopilot_publish_at: string | null | undefined
  post_approval_enabled: boolean | null | undefined
  integration_maintenance_banners?: IntegrationMaintenanceBanner[]
  is_ap_freemium: boolean
  is_ap_freemium_brand_plan_set: boolean
  referral_rewards?: {
    deferred?: number
    free?: number
    paying?: number
  }
  is_campaigns: boolean
  auto_reload_credits: boolean
  auto_reload_limit: number | null | undefined
}

export interface IntegrationMaintenanceBanner {
  provider: string
  provider_display_name: string
  message: string
  publishing_disabled: boolean
}

export interface ArticlesCountPerWorkspace {
  [key: number]: number
}

export interface Channel {
  name: string
  id: number
  accessibility: string
  workspace_wide: boolean
}

export interface AccessMemberGroup {
  id: string
  label: string
  name: string
  logo_url?: string
  members: SettingsMember[]
  channels: MinimalChannel[]
}

export interface ChannelInfo {
  accessing_as_super_admin: boolean
  avatar_url: string | null
  color: string | null
  docs_count: number
  emoji: string | null
  id: number
  key: string
  members_count: number
  name: string
  description?: string
  accessibility: string
  owner: boolean
  workspace_wide: boolean
  workspace_id: number
  members: {
    avatar_url: string | null
    company: Workspace
    confirmed: boolean
    display_name: string
    email: string
    first_name: string
    id: number
    last_name: string | null
    seo_string: string
  }[]
  group_admin_names: string[]
  default_group: boolean
}

export interface ChannelMember {
  id: number
  owner: boolean
  member: ShareUser
  position: number
}

export interface Spotlight {
  background_color: string | null
  button_color: string | null
  button_text: string | null
  button_text_color: string | null
  cta_url: string
  description: string
  emoji_icon: string | null
  headline: string
  id: number
  image_url: string
  spotlight_type: 'user' | 'article'
  title: string | null
  title_text_color: string | null
  user_image_url: string | null
}

export interface ArticleMention {
  mentionable_id: number
  mentionable_type: string
  mentioning_article_id: number
  mentioning_element_id: number
  mentioning_article_title: string
}

export interface Portal {
  description: string | null
  logo_url: string | null
  pages: PortalPage[]
  primary_color: string | null
  secondary_color: string | null
  text_color: string | null
  title: string | null
}

export interface PortalPage {
  position: number
  sections: PortalSection[]
  title: string
  id: number
}

export type CardSectionType = 'card_small' | 'card_medium' | 'card_large'
export type PeopleSectionType = 'people_hide_contact' | 'people_show_contact'
export type PortalSectionType = 'banner' | CardSectionType | PeopleSectionType | 'media'
export type SectionLayout = 'carousel' | 'grid' | ''

export interface OptionalPortalSection {
  description?: string
  items?: PortalSectionItem[]
  position?: number
  section_type?: PortalSectionType
  title?: string
  id: number
  config?: {
    layout?: SectionLayout
  }
}

export interface PortalColumn {
  id: number
  width: 1 | 2 | 3
  position: number
}

export type PortalColumnCreationAttributes = Pick<PortalColumn, 'width' | 'position'>

export interface PortalSection extends OptionalPortalSection {
  description: string
  items: PortalSectionItem[]
  columns: PortalColumn[]
  position: number
  section_type: PortalSectionType
  title: string
}

type PortalSectionItemContent = BannerContent | CardContent | PersonContent | MediaContent
export type OptionalPortalSectionItemContent =
  | OptionalBannerContent
  | OptionalCardContent
  | OptionalPersonContent
  | OptionalMediaContent

export interface PortalSectionItem {
  content: PortalSectionItemContent | OptionalPortalSectionItemContent
  position: number
  id: number
}

export interface PortalSectionBannerItem extends PortalSectionItem {
  content: OptionalBannerContent
}

export interface PortalSectionCardItem extends PortalSectionItem {
  content: OptionalCardContent
}

export interface PortalSectionPersonItem extends PortalSectionItem {
  content: OptionalPersonContent
}

export interface PortalSectionMediaItem extends PortalSectionItem {
  content: OptionalMediaContent
}

export interface OptionalBannerContent {
  title?: string
  emoji_icon?: string
  icon_background_color?: string
  cta_url?: string
}

export interface BannerContent extends OptionalBannerContent {
  title: string
  emoji_icon: string
  icon_background_color?: string
  cta_url: string
}

export interface OptionalCardContent {
  title?: string
  description?: string
  cta_url?: string
  button_text?: string
  image_url?: string
  emoji_icon?: string
  button_color?: string
  background_color?: string
}

export interface CardContent extends OptionalCardContent {
  title: string
  description: string
  cta_url: string
  button_text: string
  image_url: string
  emoji_icon: string
  button_color: string
  background_color: string
}

export type PersonContent = PersonShowContactContent | PersonHideContactContent

export interface OptionalPersonContent {
  name?: string
  image_url?: string
  role?: string
  company?: string
  location?: string
  email?: string
  linkedin_url?: string
  phone?: string
  send_to_email?: string
}

export interface PersonShowContactContent extends OptionalPersonContent {
  name: string
  image_url: string
  role: string
  email: string
  linkedin_url: string
  phone: string
}

export interface PersonHideContactContent extends OptionalPersonContent {
  name: string
  image_url: string
  role: string
  company: string
  location: string
  linkedin_url: string
  send_to_email: string
}

export interface OptionalMediaContent {
  title?: string
  description?: string
  media_url?: string
}

export interface MediaContent extends OptionalMediaContent {
  title: string
  description: string
  media_url: string
}

export interface FollowingUser {
  avatar_url: string | null
  company_name: string | null
  company_role: string | null
  display_name: string
  following_id: number | null
  id: number
}

export type SocialNetwork =
  | 'link'
  | 'email'
  | 'facebook'
  | 'linkedin'
  | 'twitter'
  | 'reddit'
  | 'github'
  | 'slack'
  | 'trello'
  | 'hackernews'
  | 'pocket'

export type EventType =
  | 'Event::ArticleFormElementAdded'
  | 'Event::ContextualEducation'
  | 'Event::CoreHeroPrimaryRegistrationCta'
  | 'Event::OnboardingEmailModalVisit'
  | 'Event::OnboardingEmailModalContinueGoogle'
  | 'Event::OnboardingEmailModalContinueEmail'
  | 'Event::OnboardingEmailModalLogin'
  | 'Event::OnboardingConfirmationModalVisit'
  | 'Event::OnboardingConfirmationModalDifferentEmail'
  | 'Event::OnboardingConfirmationModalResendConfirmation'
  | 'Event::OnboardingPasswordModalVisit'
  | 'Event::OnboardingPasswordModalTerms'
  | 'Event::OnboardingPasswordModalPrivacy'
  | 'Event::OnboardingPasswordModalContinue'
  | 'Event::OnboardingSupportClicked'
  | 'Event::OnboardingWelcomeModalVisit'
  | 'Event::OnboardingWelcomeModalSkip'
  | 'Event::OnboardingWelcomeModalPublishDoc'
  | 'Event::OnboardingWelcomeModalCategory'
  | 'Event::OnboardingWorkspaceCreated'
  | 'Event::SearchQuery'
  | 'Event::UnauthenticatedRequestedArticleFollow'
  | 'Event::SearchResultType'
  | 'Event::ProjectView'
  | 'Event::SecretIdentitySkipped'
  | 'Event::AddMoreContentClicked'
  | 'Event::BrandVoiceSkipped'
  | 'Event::BrandVoiceConfirmed'
  | 'Event::BrandVoiceModalViewed'
  | 'Event::ContentCampaignSkipped'
  | 'Event::BrainstormSkipped'
  | 'Event::ThirdPartyIntegrationsSkipped'
  | 'Event::BlogPostSkipped'
  | 'Event::BlogPostCreated'
  | 'Event::BlackFridaySkipped'
  | 'Event::GenerateContentClicked'
  | 'Event::ImproveFeedbackModalCompleted'
  | 'Event::WebsiteUrlSubmitted'
  | 'Event::BrandProfileEdited'
  | 'Event::BusinessTypeSelected'
  | 'Event::CalendarItemsDeleted'
  | 'Event::AccountDetailsSubmitted'
  | 'Event::AccountConnected'
  | 'Event::IntegrationConnected'
  | 'Event::OnboardingIntegrationConnected'
  | 'Event::TopicAdded'
  | 'Event::TopicDeleted'
  | 'Event::EditInDesignerClicked'
  | 'Event::TopicsReviewed'
  | 'Event::MarketingExperienceLevelSelected'
  | 'Event::EmailSubmitted'
  | 'Event::EmailVerified'
  | 'Event::SignupMethodSelected'
  | 'Event::KeywordsAdded'
  | 'Event::FreemiumModalCompleted'
  | 'Event::SwitchToDifferentPlatform'
  | 'Event::MobileContentPreferencesViewed'
  | 'Event::MobileStylesAndVoiceViewed'
  | 'Event::MobileSourceMaterialsViewed'
  | 'Event::MobileMediaLibraryViewed'
  | 'Event::MobileBrandProfileViewed'
  | 'Event::MobileBrandKitViewed'
  | 'Event::ExtraAccountAddedOnTrial'
  | 'Event::ExtraAccountAdded'

export type FollowSource = 'followers_panel' | 'following_panel' | 'profile_cta' | 'search_cta'

export interface SortOption {
  label: string
  key: string
}

export interface AmbiguousArticle
  extends Omit<
      ArticleFlat,
      | 'updated_at'
      | 'cover_image_url'
      | 'company'
      | 'company_name'
      | 'category_name'
      | 'made_public_at'
      | 'last_edited_by'
      | 'last_edited_at'
    >,
    Omit<
      AlgoliaArticle,
      | 'updated_at'
      | 'cover_image_url'
      | 'company'
      | 'company_name'
      | 'category_name'
      | 'made_public_at'
      | 'last_edited_at'
      | 'channel'
      | 'channel_emoji'
    >,
    Omit<RequestedArticle, 'made_public_at'> {
  updated_at: number | string
  cover_image_url: string | null
  company: WorkspaceSettings | number
  company_name: string | null
  category_name: string | null
  made_public_at: string | number | null
  last_edited_at: string | null | number
}

export interface PresentUser {
  id: number
  first_name: string | null
  last_name: string | null
  avatar_url: string | null
}

export interface ElementPresence {
  user: PresentUser | null
  anonymous_user: AnonymousUser | null
  layer_id: string
  element_uuid?: string
  head?: number
  anchor?: number
  color?: string
  following?: number
}

export interface UserElementPresences {
  current: ElementPresence
  previous?: ElementPresence
}

export interface AnonymousUser {
  uuid: string
  first_name: string
  last_name: string | null
  display_name: string
}

export type PresenceUser = PresentUser | AnonymousUser | null

export type SelectedImage = {
  url: string
  id?: string
  full_resolution_url?: string
  file?: File
  preview_url?: string
  original_filename?: string
  source_url?: string
  thumbnail_url?: string
  mime_type?: string
  duration?: number
  variants?: Array<MediaVariant | UnavailableMediaVariant>
  height?: number
  width?: number
  unsplash?: {
    authorUsername: string
    authorName: string
    id: string
    query?: string
  }
  preview_variant_url?: string
  pixabay?: {
    id?: string
  }
  media_file_id?: number
}

export type EmbedFormat = 'url' | 'card' | 'embed' | 'smartLink'

export enum EmbedProvider {
  Generic = 'generic',
  Github = 'github.com',
}

export interface GenericEmbed {
  format: EmbedFormat
  url: string
  html: string
  meta: {
    title: string
    description: string
  }
  links: {
    thumbnail: [
      {
        href: string
      },
    ]
  }
  provider: EmbedProvider
  integrationRequired: boolean
}

export interface Embed {
  sourceType: string
  src: string
  srcdom: string
  size: 's' | 'm' | 'l'
  frameborder: string
  allowfullscreen: string
  webkitallowfullscreen: string
  mozallowfullscreen: string
}

export type RenderedSVGComponent = React.ReactElement<
  React.SVGProps<SVGSVGElement> & {
    title?: string | undefined
  }
>

export interface Timer {
  name: string
  article_id: string
  started_at: number | null
  running: boolean
  elapsed: number
  segments: Array<{ name: string; length: number }>
}

export enum Pronouns {
  MALE = 'He / Him',
  FEMALE = 'She / Her',
  PLURAL = 'They / Them',
}

export interface SecretIdentity {
  identity_key: IdentityKey
  hero_image_key: ImageKey
  background_color: string
  foreground_color: string
}

export interface MemberProfile {
  full_name: string | null
  id: number
  avatar_url: string | null
  profile_image_url: string | null
  cover_image_url: string | null
  display_name: string
  current_status_icon: string | null
  current_status: string | null
  first_name: string | null
  last_name: string | null
  pronouns: string | null
  role: string | null
  email: string
  location: string | null
  timezone: string | null
  bio: string | null
  phone: string | null
  birthday: string | null
  joined_date?: string
  twitter_handle: string | null
  team?: string
  sync_profiles?: boolean
  groups?: { id: number; workspace_id: number; name: string }[]
  secret_identity: SecretIdentity | {}
  company_role: 'member' | 'admin' | 'super_admin' | 'guest' | 'unauthed'
}

export interface PublishDocSubscriptionList {
  subscriptions: PublishDocSubscription[]
}

export interface PublishDocSubscription {
  id: number
  subscription_name: string
  source: string
}

export type MinimalMember = Pick<MemberProfile, 'avatar_url' | 'display_name' | 'id'>

export enum GroupAccessType {
  PUBLIC = 'public',
  PRIVATE = 'private',
  CLOSED = 'closed',
}
export interface HighlightPreview {
  article_style: ArticleStyle | null
  elements: Record<string, any>[]
  schema_version: number
  workspace_article_style: ArticleStyle | null
}

export interface ThirdPartyIntegration {
  user_id: number
  created_at: string
  updated_at: string
  service: string
}

export type ReactSVGElement = FunctionComponent<
  React.PropsWithChildren<
    SVGProps<SVGSVGElement> & {
      title?: string | undefined
    }
  >
>

export enum ArticleTypes {
  HANDBOOK = 'handbook',
}

export enum RecipientsSelectorValueType {
  User = 'User',
  Channel = 'Channel',
  Email = 'Email',
  LayerEditors = 'LayerEditors',
  AccessMemberGroup = 'AccessMemberGroup',
  EveryoneAtWorkspace = 'EveryoneAtWorkspace',
  Company = 'Company',
  UnauthUser = 'UnauthUser',
}

export type UserType = 'User' | 'AnonymousUser'

export interface OwnershipTransfer {
  subject_type: 'Project' | 'Article'
  subject: ArticleMeta | Project
}

export enum AccessGrantAssetType {
  Article = 'Article',
  Handbook = 'Handbook',
  Layer = 'LayerAccessGrant',
}

export interface ArticleAsset {
  title: string
  seo_string: string
  id: string
}

export interface HandbookAsset {
  title: string
  seo_string: string
  id: string
}

export interface LayerAsset {
  id: string
  title: string
  article: ArticleAsset
}

export type AccessGrantAsset = ArticleAsset | HandbookAsset | LayerAsset

export interface LegacyAccessGrant {
  asset_type: AccessGrantAssetType
  asset_id: string
  title: string
  seo_string: string
}

export interface AccessGrant<Asset extends AccessGrantAsset = AccessGrantAsset> {
  asset_type: AccessGrantAssetType
  asset: Asset
  access?: string
}

export type FileType = 'folder' | 'article' | 'handbook' | 'media_file' | 'design'
export type ShareableFileType = FileType | 'layer'
export enum SettingsTab {
  General = 'general',
  Sidebar = 'sidebar',
  Preferences = 'preferences',
  Branding = 'branding',
  CustomUrl = 'custom url',
  Publish = 'publish',
  Workflow = 'statuses',
  Delete = 'delete',
}

export type AssetAccessGrant = Pick<
  AlmanacFile,
  'id' | 'title' | 'header_emoji' | 'url' | 'channel' | 'channel_emoji'
> & {
  default_folder?: boolean
  has_root_access: boolean
  sharing_mode?: LayerSharingMode
  type: ShareableFileType
}

export interface ParentAssetAccessGrant {
  parent: AssetAccessGrant
}

export type AssetAccessGrantWithParent = AssetAccessGrant & ParentAssetAccessGrant

export interface FileAccessGrant {
  id?: number
  access: Permission
  access_number: number
  higher_inherited_access?: Permission
  root_asset?: AssetAccessGrantWithParent
  password?: string
  main_layer_editors?: FileAccessGrantAccessor[]
  cannot_merge_layer?: boolean
  other_access: boolean
  asset: AssetAccessGrantWithParent
  accessor: FileAccessGrantAccessor
}

export interface FileAccessGrantAccessor {
  id: number
  type: RecipientsSelectorValueType
  name: string
  email: string
  avatar_url: string
  member_count?: number
  guest?: boolean
  is_current_user?: boolean
  workspace_members?: FileAccessGrantAccessor[]
}

export enum ArticleView {
  Editing = 'Editing',
  Commenting = 'Commenting',
  Reading = 'Reading',
}

export enum ArticleViewRequest {
  Edit = 'Request Edit Access',
  Comment = 'Request Comment Access',
  Read = 'Request Read Access',
}

declare class OpaqueTag<S extends string> {
  private tag: S
}

export type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>
    }
  : T

/**
 * Creates an opaque type when given a primitive type and unique string.
 * @see https://evertpot.com/opaque-ts-types/
 * @see http://alisabzevari.github.io/2020/01/21/2020-01-21-typescript-opaque-types/
 *
 * @example
 *   type Email = Opaque<string, 'Email'>
 *   const email: Email = 'hello'
 *   // => Type 'string' is not assignable to type 'Email'
 *
 *   const isEmail = (x: string): x is Email => /emailregex/.test(x)
 *   const maybeEmail = 'hello@hello.com'
 *   const realEmail: Email | undefined = isEmail(maybeEmail) ? maybeEmail : undefined
 */
export type Opaque<T, S extends string> = T & OpaqueTag<S>

/**
 * A function that does nothing and should never be called.
 * Useful for ensuring all possible cases are covered in switch statements.
 *
 * @example
 *
 * type Dog = {
 *   breed: 'corgi' | 'rat terrier' | 'pit bull'
 * }
 * const isGoodBoy = (dog: Dog) => {
 *   switch(dog.breed) {
 *     case 'corgi':
 *       return true
 *     default:
 *       return absurd(dog.breed) // Error: Type 'string' is not assignable to type 'never'.
 *   }
 * }
 *
 * @example
 * type Dog = {
 *   breed: 'corgi' | 'rat terrier' | 'pit bull'
 * }
 * const isGoodBoy = (dog: Dog) => {
 *   switch(dog.breed) {
 *     case 'corgi':
 *       return true
 *     case 'rat terrier':
 *       return true
 *     case 'pit bull':
 *       return true
 *     default:
 *       return absurd(dog.breed) // typeof dog.breed is 'never', so we are chill :)
 *   }
 * }
 */

export const absurd = (x: never) => {}

/**
 * Drops the first type from a tuple type.
 * Very useful when using rest args for partial application.
 *
 * @example
 * type S = DropFirst<[number, string]>
 * // => [string]
 *
 * const someFunc = (a: number, b: string, c: boolean) => {}
 * type Args = DropFirst<Parameters<typeof someFunc>>
 * const somePartiallyAppliedFunc = (...args: Args) => someFunc(1, ...args)
 */
export type DropFirst<T extends readonly unknown[]> = T extends readonly [any?, ...infer U] ? U : [...T]

/**
 * Takes the first type from a tuple type.
 * Very useful for generically extracting props from react components.
 *
 * @example
 * type S = First<[number, string]>
 * // => number
 *
 * const MyComponent = (props: MyComponentProps) => (<div>hi</div>)
 * type Props = First<Parameters<typeof MyComponent>>
 */
export type First<T extends readonly unknown[]> = T extends readonly [infer F, ...any] ? F : T
export type PropsType<T extends FunctionComponent<PropsWithChildren<any>>> = First<Parameters<T>>

export type CSSPropertyName = Extract<keyof CSSStyleDeclaration, string>

export type MinimalUserSerializerUser = {
  id: number
  email: string
  first_name: string
  last_name: string | null
  display_name: string
  avatar_url: string | null
}

export interface ReviewTemplate {
  id: number
  workspace_id: number
  name: string
  description: string
  html_description: string | null
  days_ahead: number
  privacy: ReviewPrivacy
  user_viewers: SettingsMember[]
  user_reviewers: SettingsMember[]
  user_approvers: SettingsMember[]
  channel_viewers: MinimalChannel[]
  channel_reviewers: MinimalChannel[]
  channel_approvers: MinimalChannel[]
  last_editor: MinimalUserSerializerUser | null
  created_at: string
  times_used: number
}

export type ReviewMembershipType = 'viewer' | 'reviewer' | 'approver'

export type ReviewMember = {
  membership_type: ReviewMembershipType
  id: number
  type: 'Channel' | 'User'
}

export type ReviewEmailMember = {
  membership_type: ReviewMembershipType
  email: string
}

export type Plural<T extends string> = `${T}s`

export interface ScheduledReview {
  id: number
  name: string
  privacy: ReviewPrivacy
  interval: string
  user_viewers: SettingsMember[]
  user_reviewers: SettingsMember[]
  user_approvers: SettingsMember[]
  channel_viewers: MinimalChannel[]
  channel_reviewers: MinimalChannel[]
  channel_approvers: MinimalChannel[]
  next_occurrence: string
  requester: InformalFeedbackRequestUserCard
  created_at: string
  article: InformalFeedbackRequestArticle
  type: ReviewType
}

export interface KanbanViewColumn {
  position: number
  value: string
  color: string
  property_value_id: number
  shown: boolean
}

export interface KanbanView {
  id: number
  property_id: number
  project_key: string
  has_edit_access: boolean
  columns: KanbanViewColumn[]
}

// Types are broken for latest emoji-mart, so using this in the meantime
// https://github.com/missive/emoji-mart/issues/576
export interface BaseEmoji {
  id: string
  name: string
  colons: string
  emoticons: string[]
  unified: string
  skin: 1 | 2 | 3 | 4 | 5 | 6 | null
  native: string
}

export interface ArticleTodosGroupArticle {
  key: string
  url: string
  title: string
  slug: string
  channel_emoji?: ChannelType | null
  header_emoji: string | null
  external_file_icon: string | null
  informal_feedback_request_id?: number
}

export interface ArticleTodosGroup {
  todos_count: number
  comments_count: number
  due_date: string
  article: ArticleTodosGroupArticle
  __type: 'ArticleTodosGroup'
}

export enum SuggestionRequestStatus {
  Pending = 'pending',
  Processing = 'processing',
  Successful = 'successful',
  Failed = 'failed',
  Aborted = 'aborted',
  Expired = 'expired',
  Started = 'started',
}

export interface PromptRequest<Prompt = { [key: string]: any }> {
  prompt: Prompt
  selection: Selection | null
}

export interface SuggestionRequest<Prompt = { [key: string]: any }> extends PromptRequest<Prompt> {
  id: number
  status: SuggestionRequestStatus
  layer_id: string
  article_id: string
  job_id: string | null
  type: 'generate' | 'revise'
  generated_content: string | null
  uuid: string
  almanac_file?: AlmanacFileSummary & {
    external_integration_id?: number
    parent?: {
      id: string
      seo_string: string | null
    }
  }
  rated: boolean
}

export interface Selection {
  anchor: number
  head?: number
  type: 'text' | 'node'
}

export interface CompletedSuggestionRequest extends SuggestionRequest {
  offset: number | undefined
}

export interface SuggestionRequestWithRequester extends SuggestionRequest {
  requester: MinimalUserSerializerUser
}

export interface BatchSuggestionRequestSummary {
  id: number
  status: SuggestionRequestStatus
}

export interface BatchSuggestionRequest extends BatchSuggestionRequestSummary {
  requester: MinimalUserSerializerUser
  requests: SuggestionRequest[]
  project: Project
}

export interface ContentCampaign {
  id: number
  name: string
  focus?: string
  project_id: number
  owner: MinimalUserSerializerUser
  brand_voice_id?: number
  seo_string: string
}

export type ArticleGenerationType = ChannelType | GenerationType

export enum VisualChannelType {
  Instagram = 'instagram_post_design',
}

export interface GeneratePrompt {
  generation_type: ArticleGenerationType
  brand_voice_id?: number
  tone?: Tone[]
  seo_words?: string[]
  length?: string
  ideas_count?: IdeasCount
  content_type?: string
  selected_content?: string
  action?: string
  format?: string
  language?: string
  // Wizard attributes
  goal?: string
  audience?: string
  call_to_action?: string
  author_persona?: string
  product_description?: string
  product_name?: string
  avoid?: string
  additional_info?: string
  introduction?: string
  outline?: string
  topic?: string
  count?: number
  case_study_customer_name?: string
  case_study_challenges?: string
  case_study_solutions?: string
  case_study_outcomes?: string
  include_images?: boolean
  title?: string
  blog_length?: string
  video_length?: string
  source_material_ids?: number[]
  reference_text?: string
  product_features?: string[]
  caption_count?: number
  caption_length?: number
  transcription_id?: number
  cross_post_accounts?: CrossPostAccount[]
  external_integration_id?: number
  brand_kit_id?: number
  caption_type?: string
  selected_images?: string[]
  featured_selected_images?:
    | Array<{
        url: string
        alt_text: string
      }>
    | string[]
  external_links?: string[]
  include_emojis?: boolean
}

export interface VisualGeneratePrompt {
  generation_type: VisualChannelType
  topic: string
  brand_visual_kit_id?: number
  brand_voice_id?: number
  brand_kit_id?: number
}

export interface ImagePrompt {
  generation_type: GenerationType.Image
  topic: string
  image_type: string
  image_aspect_ratio: string
  priority: string
  image_style?: string
  image_mood?: string
}

export interface GenerateRequest extends SuggestionRequest<GeneratePrompt> {}
export interface VisualGenerateRequest extends SuggestionRequest<VisualGeneratePrompt> {}
export interface ImageGenerateRequest extends SuggestionRequest<ImagePrompt> {}
export interface ImageGenerateFromBrandProfileRequest extends SuggestionRequest<{}> {}

export interface GeneratedImage {
  url: string
  w: number
  h: number
  mimeType: string
  revisedPrompt: string
}

export type ImageGenerateRequestGeneratedContent = GeneratedImage[]

export enum KnowledgeBaseSnippetKind {
  General = 'general',
  ProductOrServiceDescription = 'product_or_service_description',
  CompanyInfo = 'company_info',
  AvoidList = 'avoid_list',
  Keywords = 'keywords',
  Audience = 'audience',
}

export enum KnowledgeBaseSourceMaterialKind {
  Text = 'text',
  Article = 'article',
  Url = 'url',
  File = 'file',
}

export interface KnowledgeBaseSnippet {
  id: number
  name: string
  content: string
  use_count: number
  emoji?: string
  updated_at: string
  default: boolean
  owner: {
    avatar_url: string
    display_name: string
  }
  kind: KnowledgeBaseSnippetKind
}
export interface KnowledgeBaseSourceMaterial {
  id: number
  name: string
  content: string
  emoji: string | null
  created_at: string
  updated_at: string
  color: string | null
  kind: KnowledgeBaseSourceMaterialKind
  owner: {
    avatar_url: string
    display_name: string
  }
  source_url: string | null
  source_article: BareArticle | null
  source_file_filename: string | null
  source_file_url: string | null
  screenshot?: string
}

export type CustomPrompt = {
  custom_prompt: string
  revision_type: RevisionType.CUSTOM_PROMPT
}

export type ChangeLength = {
  revision_type: RevisionType.CHANGE_LENGTH
  action: RevisionAction
}

export type ChangeToneBrandVoice = {
  revision_type: RevisionType.CHANGE_TONE
  brand_voice_id: number
  revision_level: RevisionLevel
}

export type ChangeTone = {
  revision_type: RevisionType.CHANGE_TONE
  tone: Tone[]
  revision_level: RevisionLevel
}

export type Translate = {
  revision_type: RevisionType.TRANSLATE
  language: string
}

export type CustomDesign = {
  custom_prompt: string
  revision_type: RevisionType.CUSTOM_DESIGN
}

export type RevisionPrompt = CustomPrompt | ChangeLength | ChangeToneBrandVoice | ChangeTone | Translate | CustomDesign

export interface Revision<Prompt = RevisionPrompt> {
  id: string
  content: string
  completed_at: string | null
  failed_at: string | null
  started_at: string | null
  accepted_at: string | null
  discarded_at: string | null
  failure_reason: string | null
  completed: boolean
  started: boolean
  failed: boolean
  pending: boolean
  job_id: string | null
  previous_revision: Revision | null
  layer: LayerSummary
  prompt: Prompt
}

export interface AISuggestionNode {
  id: string
  request_id: string
}

export interface VisualStyle {
  id: number
  name: string
  cover_image_url: string
  model_id?: string
}

export interface BrandVisualKit {
  id: number
  name: string
  description: string | null
  image_descriptions: string[]
  workspace_id: number
  emoji: string | null
  primary_color_palette: ColorPalette
  primary_logo: BrandMediaFile | null
  logos: BrandMediaFile[]
  text_styles: TextStyle[]
  brand_visual_analysis_id: number | null
  brand_visual_analysis?: BrandVisualAnalysis | null
  brand_visual_enhancement_id?: number | null
  visual_style_id?: number | null
  visual_style?: VisualStyle | null
}

export enum BrandMediaType {
  LOGO = 'logo',
}

export enum LogoVariant {
  Original = 'Original',
  Light = 'Light',
  Dark = 'Dark',
}

export interface BrandMediaFile<Type extends BrandMediaType = BrandMediaType.LOGO> {
  id: number
  name: string
  url: string
  brand_visual_kit_id: number
  brand_media_type: Type
  media_file: Required<Pick<AlmanacFile, 'id' | 'source_url' | 'mime_type' | 'variants' | 'key'>> &
    Pick<AlmanacFile, 'width' | 'height'>
  logo_variant?: LogoVariant
  is_primary_logo?: boolean
}

export interface Color {
  name?: string
  hex: string
}

export interface ColorPalette {
  id: number
  name: string
  primary: boolean
  colors: Color[]
  brand_visual_kit_id: number
}

export enum FontRole {
  Title = 'title',
  Subtitle = 'subtitle',
  Body = 'body',
}

export type TextAlignType = 'left' | 'center' | 'right' | 'justify'

export interface FontStyle {
  font_size: number | string
  color: string
  italic: boolean
  font_weight: FontWeightType
  font_family: VisualEditorFontFamilyType
  underline: boolean
  strikethrough: boolean
  uppercase: boolean
  letter_spacing: number
  line_height: number | string
  align: TextAlignType
}

export interface TextStyle {
  id: number
  name: string
  brand_visual_kit_id: number
  style: Partial<FontStyle>
  font_role: FontRole
}

export interface Font {
  id: number
  name: string
  source_url: string
  font_family: string
  font_subfamily: string
}

export enum BrandStyleExtractionStatus {
  Failed = 'Failed',
  Completed = 'Completed',
  Started = 'Started',
  Pending = 'Pending',
}

export interface BrandStyleExtraction {
  id: number
  url: string
  status: BrandStyleExtractionStatus
  brand_visual_kit_id: number | null
  workspace_id: number
  brand_visual_kit: BrandVisualKit | null
}

export interface CompletedBrandStyleExtraction extends BrandStyleExtraction {
  status: BrandStyleExtractionStatus.Completed
  brand_visual_kit_id: number
  brand_visual_kit: BrandVisualKit
}

export enum ShieldKey {
  Academy = 'academy',
  Brand = 'brand',
  Generation = 'generation',
  Campaign = 'campaign',
  Schedule = 'schedule',
  SocialAndSeo = 'social_and_seo',
}

export interface SuperShield {
  id: number
  key: ShieldKey
  objectives: Objective[]
}

export enum ObjectiveKey {
  Register = 'register',
  CreateBrandVoice = 'create_brand_voice',
  CreateBlogContent = 'create_blog_content',
  CreateSocialContent = 'create_social_content',
  CreateBlogOrSocialContent = 'create_blog_or_social_content',
  CreateCampaign = 'create_campaign',
  PostSocialContent = 'post_social_content',
  SocialAndSeo = 'social_and_seo_planning',
}

export interface Objective {
  id: number
  key: ObjectiveKey
  completed: boolean
  super_shield?: Omit<SuperShield, 'objectives'>
}

export enum TutorialCategory {
  QuickQuest = 'quick_quest',
  BusinessBooster = 'business_booster',
  MasterSuperpowers = 'master_superpowers',
  Home = 'home',
}

export interface Tutorial {
  id: number
  title: string
  video_url: string
  video_id: string
  thumbnail_url: string
  duration: number
}

export enum AIJobStatus {
  Failed = 'failed',
  Successful = 'successful',
  Started = 'started',
  Pending = 'pending',
  Expired = 'expired',
}

export type SectionKey = 'social' | 'video' | 'seo' | 'email'

export interface PreviewTopicsResponse {
  status: 'ready' | 'processing' | 'failed'
  sections: SectionKey[]
  topics: Partial<BrandPlanTopics> | null
  error: string | null
  expires_at: string | null
}

export interface PreviewTopicsListResponse {
  previews: PreviewTopicsResponse[]
}

export interface FieldsetCompletion {
  id: number
  workspace_id: number
  prompt: string
  generation_type: string
  fields: Array<string>
  completed_fields: {
    [field: string]: any
  }
  status: AIJobStatus
}

export type Nullish<T> = T | null | undefined

export type MediaObject = {
  id: number
  source_url: string
  mime_type: string
  width?: number
  height?: number
  duration?: number
  size?: number
  frame_rate?: number
  aspect_ratio?: number
  thumbnail_url?: string
  position: number
  provider?: string
  last_used_at?: string | null
  audio?: {
    provider: string
    media_file_key: string
    tracks?: AudioTrack[]
  } | null
  anchor_content?: {
    text?: string
    image?: string
    anchor_type?: AnchorType
  }
  provider_data?: {
    generation_type?: string
    topic?: string
    original_image_url?: string
  }
}

export type AnchorContent = {
  id: number
  anchor_type: AnchorType
  text?: string
  image?: string
  content_status: AIJobStatus
  analysis: AnchorContentAnalysis
}

export type TextChannel = {
  /** Name of the text channel (ie. Instagram post)*/
  name: string
  /** Icon of the text channel */
  icon: Icon
  /** Image of the text channel */
  image: string
  tags: string[]
  /** name of the channel which can be displayed and used within a string */
  displayName?: string
}

export enum TurnInto {
  DocsIntoContent = 'docs_into_content',
  VideoIntoContent = 'video_into_content',
  WebsitesIntoContent = 'websites_into_content',
  AudioIntoContent = 'audio_into_content',
}

export type TextGenerationType =
  | GenerationType.Campaign
  | GenerationType.Brainstorm
  | GenerationType.Outline
  | GenerationType.ContentBrief
  | GenerationType.SEOPlanning
  | GenerationType.TwitterPlanning
  | GenerationType.InstagramPlanning
  | GenerationType.FacebookPlanning
  | GenerationType.LinkedInPlanning
  | GenerationType.ContentPlan

export type TextChannelKey = TurnInto | TextGenerationType | ChannelType

export type VisualChannelKey = DesignChannelType

export interface BrandVisualAnalysisFont {
  family: string
  weight: string
  style: string
}

export interface BrandVisualAnalysisFontPairing {
  display_font: BrandVisualAnalysisFont
  body_font: BrandVisualAnalysisFont
}

export interface BrandVisualAnalysisResult {
  visual_identity_description?: string
  visual_identity_keywords?: string[]
  logo?: string
  colors: string[]
  font_pairings: Array<BrandVisualAnalysisFontPairing>
  recommend_styles_and_fonts_request_id?: number
}

export interface BrandVisualAnalysis {
  id: number
  status: 'pending' | 'successful' | 'failed' | 'started' | 'aborted'
  result: BrandVisualAnalysisResult
  failure_reason: 'not_enough_content' | null
}

export interface BrandVoiceAnalysis {
  id: number
  status: AIJobStatus
  result: VoiceParams | {}
  failure_reason: 'not_enough_content' | null
}

export interface SuccessfulBrandVoiceAnalysis extends BrandVoiceAnalysis {
  status: AIJobStatus.Successful
  result: VoiceParams
  failure_reason: null
}

export interface ImageQueryGeneration {
  id: number
  status: 'pending' | 'successful' | 'failed' | 'started' | 'aborted'
  result: string | null
}

export interface TemplateBinder {
  id: number
  name: string
  designs: Array<TemplateBinderDesign>
}

export interface TemplateBinderDesign {
  id: string
  width: number
  height: number
  preview_url: string
}

export type RequireAtLeastOne<T, Keys extends keyof T = keyof T> = Keys extends keyof T
  ? Pick<T, Keys> & Partial<Omit<T, Keys>>
  : never

export interface SourceMaterialScraping {
  id: number
  status: AIJobStatus
  failure_reason?: string | null
}

interface SourceMaterial {
  id: number
  text: string
}

export type SourceType =
  | 'Website'
  | 'PlainText'
  | 'AlmanacFile'
  | 'SourceMaterialsMediaObject'
  | 'ExampleBrand'
  | 'ExternalIntegration'

interface BaseSource {
  id: number
  type: SourceType
  pending_or_started_source_material_scraping?: SourceMaterialScraping
  source_materials?: SourceMaterial[]
  created_at: string
}

export interface ExampleBrand extends BaseSource {
  type: 'ExampleBrand'
  name: string
  description: string
  logo_url: string
  brandVisualKit?: BrandVisualKit
}

export interface PlainText extends BaseSource {
  type: 'PlainText'
  text: string
}

export interface Website extends BaseSource {
  type: 'Website'
  screenshot_url?: string
  favicon?: string
  url: string
  title?: string
  source_materials: SourceMaterial[]
}

export interface SourceMaterialsMediaObject extends BaseSource {
  type: 'SourceMaterialsMediaObject'
  source_url: string
  mime_type: string
  provider?: string
}

export interface AlmanacFileSource extends BaseSource {
  type: 'AlmanacFile'
  name: string
  preview_url?: string
}

export type Source =
  | PlainText
  | Website
  | ExampleBrand
  | SourceMaterialsMediaObject
  | AlmanacFileSource
  | ExternalIntegration

export type VoiceParams = {
  purpose: string[]
  audience: string[]
  tone: string[]
  emotions: string[]
  character: string[]
  syntax: string[]
  language: string[]
  name: string
  emoji?: string | null
}

export interface BrandVoice {
  id: number
  workspace_id: number
  voice_params: VoiceParams
  created_at: string
  color: string
}

export interface Feedback {
  id: number
  rating: string
  feedback?: any
}

export type BrandKitMediaPriority = 'only_my_brand_kit' | 'brand_kit_first' | 'only_stock'
export type FeaturedMedia = 'video' | 'image' | 'text'
export type VideoSource = 'animated_image' | 'brand_kit_stock'
export type VideoTemplateCategory = 'video_only' | 'video_with_text_and_color'
export type ImageTemplateCategory =
  | 'image_only'
  | 'image_with_text'
  | 'image_with_text_and_color'
  | 'image_with_text_and_logo'

export interface ContentPreferences {
  language?: string
  topics_to_avoid?: string[]
  words_and_phrases_to_avoid?: string[]
  hashtags_to_exclude?: string[]
  hashtags_to_include?: string[]
  hashtag_frequency?: number
  logo_frequency?: number
  call_to_action_links?: string[]
  call_to_action_copy?: string
  call_to_action_frequency?: number
  social_cta_frequency?: number
  blog_cta_frequency?: number
  social_cta_link?: string
  social_cta_copy?: string
  social_without_links_cta_copy?: string
  blog_cta_link?: string
  blog_cta_copy?: string
  domains_to_avoid?: string[]
  media_priority?: BrandKitMediaPriority
  image_media_priority?: BrandKitMediaPriority
  video_media_priority?: BrandKitMediaPriority
  videos_include_music?: boolean
  blog_always_include_images?: boolean
  blog_always_include_external_links?: boolean
  featured_media?: FeaturedMedia[]
  featured_media_video_weight?: number
  featured_media_image_weight?: number
  featured_media_text_weight?: number
  brand_kit_media_reuse_weeks?: number | null
  brand_kit_image_reuse_weeks?: number | null
  brand_kit_video_reuse_weeks?: number | null
  blog_keywords?: string[]
  apply_visual_style?: boolean
  video_sources?: VideoSource[]
  video_source_brand_kit_stock_weight?: number
  video_source_animated_image_weight?: number
  video_templates?: VideoTemplateCategory[]
  image_templates?: ImageTemplateCategory[]
  primary_market_locations?: []
  audience_min_age?: number
  audience_max_age?: number
  audience_gender?: string
  representation_age_range?: string
  representation_gender?: string
  representation_culture?: string
  representation_details?: string
}

export interface BrandKit {
  id: number
  name: string
  external_integrations: ExternalIntegration[]
  websites: Website[]
  brand_voice: BrandVoice | null
  brand_visual_kit: BrandVisualKit | null
  brand_visual_kit_id?: number | null
  brand_profile?: BrandProfile | null
  media_files: Array<IMediaFile>
  status: AIJobStatus
  custom_source_materials_count: number
  analyzed_files_count: number
  analyzed_posts_count: number
  analyzed_posts_at: string
  feedback?: Feedback
  incomplete: boolean
  content_preferences?: ContentPreferences
  bad_image_detection_completed?: boolean
  brain_social_strategic_recommendations_bid: string | null
  brain_seo_strategic_recommendations_bid: string | null
  brain_email_strategic_recommendations_bid: string | null
}

export interface BrandKitShow extends BrandKit {
  workspace_id: number
  websites: Website[]
  brand_kit_sources: BrandKitSource[]
}

export type SourceMaterialCategory =
  | 'website'
  | 'website_auto'
  | 'blog'
  | 'document'
  | 'email'
  | 'other'
  | 'external_integration'

export interface BrandKitSource<T = Source | ExternalIntegration> {
  id: number
  brand_kit_id: number
  emoji: string | null
  name: string | null
  category: SourceMaterialCategory
  created_at: string
  updated_at: string
  source: T
}

export interface Export {
  status: AIJobStatus
  id: number
  file_url: string | null
  output?: DesignPreview[]
}

export type AnchorContentType = 'MediaObject' | 'Website'

export interface AnchorContentAnalysis {
  id: number
  status: AIJobStatus
  result: string | {}
  failure_reason: string | null
  job_id: string | null
  anchor_content: {
    type: AnchorContentType
    id: number
  }
}

export interface BrandProfile {
  id: number
  url: string
  profile: string | {}
  brand_plan: Omit<BrandPlan, 'brand_profile'>
  keywords: string[]
  ideal_customer_profiles: string[]
  name: string
  brand_kit: BrandKit
  latest_analysis_id: number | null
}

export interface BrandProfileAnalysis {
  id: number
  status: AIJobStatus
  result: string | {}
  failure_reason: string | null
  job_id: string | null
}

export interface BrandPlanQuestions {
  id: number
  questions: string[]
  status: AIJobStatus
  failure_reason: string | null
}

export interface Theme {
  id: string
  title: string
  description: string
  type?: 'Promotional' | 'Educational' | 'Community'
}

export interface BrandPlanThemes {
  id: number
  themes: Theme[]
  status: AIJobStatus
  failure_reason: string | null
}

export interface BrandPlanPreparation {
  id: number
  status: AIJobStatus
  result: {}
  failure_reason: string | null
}

export interface BrandPlanSummary {
  id: number
  status: AIJobStatus
  summary: string | null
  full_summary: string | null
  job_id: string | null
}

export enum BrandPlanTactic {
  Instagram = 'instagram',
  InstagramReel = 'instagram_reel',
  LinkedIn = 'linkedin',
  Facebook = 'facebook',
  Twitter = 'twitter',
  YouTube = 'youtube',
  SEO = 'seo',
  Email = 'email',
  TikTok = 'tiktok',
  MetaAds = 'meta_ads',
}

// All possible BrandPlanIntegrations values
export const ALL_BRAND_PLAN_INTEGRATIONS = [
  'instagram',
  'instagram_reel',
  'facebook',
  'linkedin',
  'twitter',
  'tiktok',
  'youtube',
  'seo',
  'email',
  'google',
  'shopify',
  'meta_ads',
] as const

export type BrandPlanIntegrations = (typeof ALL_BRAND_PLAN_INTEGRATIONS)[number]

export const BRAND_PLAN_SOCIAL_MEDIA_CHANNELS = ['instagram', 'facebook', 'linkedin', 'twitter'] as const
export type BrandPlanSocialMediaChannels = (typeof BRAND_PLAN_SOCIAL_MEDIA_CHANNELS)[number]

export type BrandPlanChannelGroup = BrandPlanSocialMediaChannels | 'email' | 'seo'

export type BrandPlanOutput = {
  drafted: number
  posted: number
}

export type BrandPlanOutputStats = {
  [key in BrandPlanChannelGroup]: BrandPlanOutput
}

export interface ContentCount {
  type: string
  count: number
}

export interface BrandPlanTacticContents extends Array<ContentCount> {}

export type DailyFrequency = 'high' | 'once_a_day'
export type CrosspostFrequency = 'never' | 'sometimes' | 'always'

export interface BrandPlanTopics {
  instagram_topics: string[] | null
  facebook_topics: string[] | null
  linkedin_topics: string[] | null
  twitter_topics: string[] | null
  email_topics: string[]
  seo_topics: string[] | null
  tiktok_topics: string[] | null
  youtube_topics: string[] | null
  crosspost_topics: string[] | null
  video_crosspost_topics: string[] | null
}

export interface BrandPlanContents {
  instagram_contents: BrandPlanTacticContents
  facebook_contents: BrandPlanTacticContents
  linkedin_contents: BrandPlanTacticContents
  twitter_contents: BrandPlanTacticContents
  email_contents: BrandPlanTacticContents
  seo_contents: BrandPlanTacticContents
  tiktok_contents: BrandPlanTacticContents
  youtube_contents: BrandPlanTacticContents
  crosspost_contents: BrandPlanTacticContents
  video_crosspost_contents: BrandPlanTacticContents
}

export interface BrandPlanSchedulePlan extends BrandPlanTopics, BrandPlanContents {
  week_days: WeekDays
  daily_frequency: DailyFrequency
  approval_date: string
  crosspost_frequency: CrosspostFrequency
  tactics: BrandPlanTactic[]
  start_date: string
}

export type WeekDay = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday'
export type WeekDays = Array<WeekDay>

export interface Strategy {
  type: BrandPlanStrategyType
}

export enum BrandPlanStrategyType {
  Evergreen = 'EvergreenStrategy',
  Pyramid = 'PyramidStrategy',
  Crosspost = 'CrosspostStrategy',
}

export interface AIQuestion {
  question: string
  answer: string
}

export type StrategyAnchorContent = Partial<Website | MediaObject> & {
  id: number
  type: AnchorContentType
}

export interface CrosspostStrategy extends Strategy {
  type: BrandPlanStrategyType.Crosspost
  theme: Theme | null
  themes: Theme[]
  anchor_content?: (Website | MediaObject) & {
    url: string | null
    type: AnchorContentType
  }
  call_to_action?: string
  target_link: string
}

export interface EvergreenStrategy extends Strategy {
  type: BrandPlanStrategyType.Evergreen
}

export interface PyramidStrategy extends Strategy {
  type: BrandPlanStrategyType.Pyramid
  anchor_content?: {
    type: AnchorContentType
    id: number
    favicon?: string
    url?: string
    source_url?: string
    mime_type?: string
  }
  lead_url?: string
  call_to_action?: string
  story?: string
}

export type AnchorType = 'blog' | 'website' | 'document' | 'video' | 'audio'

export interface BrandPlanStrategy {
  name: string
  description: string
}

export type BlogPlatformIntegration =
  | ExternalIntegrationEnum.WordPress
  | ExternalIntegrationEnum.GoHighLevel
  | ExternalIntegrationEnum.Wix
  | ExternalIntegrationEnum.Shopify
  | 'generic'

export type PaidAdsIntegration = ExternalIntegrationEnum.MetaAds

export type SelectedBlogPlatform = BlogPlatformIntegration | null

export type EmailPlatformIntegration = ExternalIntegrationEnum.Mailchimp | 'generic'

export type SelectedEmailPlatform = EmailPlatformIntegration | null

interface BrandPlanBase extends BrandPlanSchedulePlan {
  id: number
  workspace_id: number
  brand_profile_id: number
  length: number
  intensity: 10 | 20 | 30 | 40
  tactics: BrandPlanTactic[]
  instagram_avg_daily_impressions: number | null
  facebook_avg_daily_impressions: number | null
  linkedin_avg_daily_impressions: number | null
  twitter_avg_daily_impressions: number | null
  email_avg_open_rate: number | null
  email_avg_click_through_rate: number | null
  seo_avg_daily_visits: number | null
  created_at: string
  updated_at: string
  brand_profile: Omit<BrandProfile, 'brand_plan'>
  brand_kit: BrandKit
  output_stats: BrandPlanOutputStats
  start_date: string
  started_at?: string | null
  days_before_review: number
  timezone: string
  schedule_length?: number
  selected_blog_platform: BlogPlatformIntegration | null
  selected_email_platform: EmailPlatformIntegration | null
  weekly_ads_budget?: number
  generation_status?: SuggestionRequestStatus
  active: boolean
  schedules_reviewed: boolean
}

export interface BrandPlanEvergreen extends BrandPlanBase {
  strategy_type: BrandPlanStrategyType.Evergreen
  strategy: EvergreenStrategy
}

export interface BrandPlanCrosspost extends BrandPlanBase {
  strategy_type: BrandPlanStrategyType.Crosspost
  strategy: CrosspostStrategy
}

export interface BrandPlanPyramid extends BrandPlanBase {
  strategy_type: BrandPlanStrategyType.Pyramid
  strategy: PyramidStrategy
}

export interface BrandPlanNoStrategy extends BrandPlanBase {
  strategy_type?: undefined
  strategy?: undefined
}

export type BrandPlan = BrandPlanNoStrategy | BrandPlanEvergreen | BrandPlanCrosspost | BrandPlanPyramid

export interface BrandPlanScheduleContent {
  id: number
  brand_plan_schedule_id: number
  status: SuggestionRequestStatus
  request?: SuggestionRequest
  created_at: string
  topic: string
  type: string
  post_at: string
  url?: string | null
  almanac_file?: AlmanacFileCalendarItem
  preview_url?: string | null
  viewed_at?: string | null
  is_crosspost?: boolean
}

export type ScheduledStatus = 'planned' | 'scheduled' | 'publishing' | 'published' | 'paused'

interface BrandPlanScheduleBase extends Omit<BrandPlanSchedulePlan, 'approval_date'> {
  id: number
  brand_plan_id: number
  project: Project
  contents: Array<BrandPlanScheduleContent>
  status: SuggestionRequestStatus
  regenerating_topic: boolean
  start_date: string
  approval_date: string | undefined
  length: number
  created_at: string
  tactics: BrandPlanTactic[]
  end_date: string
  scheduled_status: ScheduledStatus
  is_preparing: boolean
  is_current: boolean
  title: string | undefined
  audience: string | undefined
  goal: string | undefined
  cover_image: string | undefined
  blog_platform: SelectedBlogPlatform
  media_files: Array<IMediaFile & { media_file_id: number }>
  attaching_media: boolean | null
  reviewed: boolean
  questions: AIQuestion[]
  unreviewed_contents_count: number
  theme: Theme | null
  call_to_action?: string | null
  target_link?: string | null
  sent_for_approval: string | null
}

export interface CampaignSource<T = Source> {
  id: number
  updated_at: string
  created_at: string
  campaign_id: number
  source: T
}

export interface BrandPlanScheduleEvergreen extends BrandPlanScheduleBase {
  strategy_type: BrandPlanStrategyType.Evergreen
  strategy: EvergreenStrategy
}

export interface BrandPlanScheduleCrosspost extends BrandPlanScheduleBase {
  strategy_type: BrandPlanStrategyType.Crosspost
  strategy: CrosspostStrategy
  questions: AIQuestion[]
}

export interface BrandPlanSchedulePyramid extends BrandPlanScheduleBase {
  strategy_type: BrandPlanStrategyType.Pyramid
  strategy: PyramidStrategy
  questions: AIQuestion[]
}

export interface BrandPlanScheduleNoStrategy extends BrandPlanScheduleBase {
  strategy_type?: undefined
  strategy?: undefined
}

export type BrandPlanSchedule =
  | BrandPlanScheduleNoStrategy
  | BrandPlanScheduleEvergreen
  | BrandPlanScheduleCrosspost
  | BrandPlanSchedulePyramid

export interface CampaignPreparationResult {
  audience: string
  goal: string
  description: string
  callToAction: string
  title: string
}

export interface CampaignPreparation {
  id: number
  status: AIJobStatus
  result?: CampaignPreparationResult
  cover_image: string | null
}

export interface BatchCampaignPreparation {
  id: number
  status: AIJobStatus
  result?: VariablesOf<BrandPlanScheduleCrosspostInput>[]
}

interface BrandVisualEnhancementFont {
  style: string
  family: string
  weight: number
}

export interface BrandVisualEnhancementResult {
  suggested_font_pairings: {
    body_font: BrandVisualEnhancementFont
    display_font: BrandVisualEnhancementFont
    reasoning: string[]
  }[]
  suggested_color_palettes: {
    colors: string[]
    reasoning: string[]
  }[]
}
export interface BrandVisualEnhancement {
  id: number
  brand_visual_kit_id: number
  input: Record<string, string>
  result: BrandVisualEnhancementResult | null
  status: AIJobStatus
}

export enum Archetype {
  INSTAGRAM_POST_PHOTO_TEXT = 'instagram_post_photo_text',
  INSTAGRAM_POST_PHOTO_TEXT_LOGO = 'instagram_post_photo_text_logo',
  INSTAGRAM_STORY_PHOTO_TEXT = 'instagram_story_photo_text',
  INSTAGRAM_HEADER_GRADIENT_BACKGROUND = 'instagram_header_gradient_bg',
  PHOTO = 'photo',
  VIDEO = 'video',
  VIDEO_INSTAGRAM_TEXT = 'video_instagram_text',
  VIDEO_HEADER_PARAGRAPH_BOXED = 'video_header_paragraph_boxed',
  HEADER_LOGO = 'header_logo',
  HEADER_PARAGRAPH = 'header_paragraph',
  HEADER_PARAGRAPH_PHOTO = 'header_paragraph_photo',
  HEADER_PHOTO = 'header_photo',
  PHOTO_HEADER_PARAGRAPH = 'photo_header_paragraph',
  PHOTO_PARAGRAPH = 'photo_paragraph',
  PHOTO_HEADER_PARAGRAPH_BOXED = 'photo_header_paragraph_boxed',
}

export interface CrossPostGroup {
  id: number
  batch_id: string
  almanac_files: AlmanacFileCalendarItem[]
}

export interface Batch {
  is_complete: boolean
  bid: string
  total: number
  pending: number
  description: string
  failures: number
  created_at: number
  fail_info: any[]
}

export enum MetaAdCampaignObjective {
  OUTCOME_APP_PROMOTION = 'OUTCOME_APP_PROMOTION',
  OUTCOME_AWARENESS = 'OUTCOME_AWARENESS',
  OUTCOME_ENGAGEMENT = 'OUTCOME_ENGAGEMENT',
  OUTCOME_LEADS = 'OUTCOME_LEADS',
  OUTCOME_SALES = 'OUTCOME_SALES',
  OUTCOME_TRAFFIC = 'OUTCOME_TRAFFIC',
}

export enum MetaAdCampaignSpecialAdCategory {
  NONE = 'NONE',
  EMPLOYMENT = 'EMPLOYMENT',
  FINANCIAL_PRODUCTS_SERVICES = 'FINANCIAL_PRODUCTS_SERVICES',
  HOUSING = 'HOUSING',
  ISSUES_ELECTIONS_POLITICS = 'ISSUES_ELECTIONS_POLITICS',
}

export type MetaAdCampaignLocation = {
  key: string
  name: string
  type: 'city' | 'country' | 'region'
  country_code: string
  country_name: string
  region?: string
}

export type MetaAdCampaignGeoLocations = Record<'cities' | 'countries' | 'regions', MetaAdCampaignLocation[]>

export type MetaAdCampaign = {
  id: number

  ad_call_to_action?: string | null
  ad_caption?: string | null
  ad_headline?: string | null
  ad_topic?: string | null
  age_max: number
  age_min: number
  currency_code: string
  daily_budget_offsetted: number
  end_time?: string
  gender_name: string
  gender: number
  geo_locations: MetaAdCampaignGeoLocations
  link_to_promote?: string
  locale_names: string[]
  locales: string[]
  objective?: MetaAdCampaignObjective | null
  pixel_conversion_event_name?: string | null
  pixel_platform_id?: string | null
  platform_id?: string
  special_ad_categories: MetaAdCampaignSpecialAdCategory[]
  start_time?: string
  status: string

  created_at: string
  updated_at: string
}

export type MetaAdCampaignInsights = {
  id: string
  objective?: MetaAdCampaignObjective
  ads?: { data: MetaAdInsights[] }
  insights: { data: MetaAdCampaignInsight[] }
}

export type MetaAdInsights = {
  id: string
  insights: { data: MetaAdInsight[] }
}

export type MetaAdCampaignInsight = {
  account_currency?: string
  spend?: string
  results?: MetaAdsInsightResult[]
  cost_per_result?: MetaAdsInsightResult[]
}

export type MetaAdInsight = {
  spend?: string
  results?: MetaAdsInsightResult[]
  cost_per_result?: MetaAdsInsightResult[]
}

export type MetaAdsInsightResult = {
  indicator: string
  values: Array<{ value: string }>
}

export type AdCampaignStatus = 'active' | 'draft' | 'error' | 'paused' | 'publishing'

export type AdCampaign = {
  id: number
  name: string
  platform: string
  company_id: number
  campaignable: MetaAdCampaign
  created_at: string
  updated_at: string
  ads?: Ad[] | null
  auto_pause?: boolean | null
  status?: AdCampaignStatus
}

export type AdStatus = 'Active' | 'Paused' | 'Error' | 'Draft' | 'Published' | 'Publishing'

export type Ad = {
  id: number
  ad_campaign_id: number
  design_id?: number | null
  design?: null | {
    id: number
    title: string
    content: any
    height: number
    width: number
    preview_url?: string
    url?: string
  }
  latest_ai_suggestion_request?: null | {
    id: number
    status: AIJobStatus
    generated_content: string
    prompt: Record<string, any>
    started_at?: string | null
    created_at: string
  }
  media_object?: MediaObject | null
  platform: string | null
  creative_platform_id?: string | null
  platform_id?: string | null
  is_selected: boolean
  status: AdStatus
  created_at: string
  updated_at: string
  winner: boolean
}

export type ScheduledPostTarget = {
  id: number
  status: 'pending' | 'queued' | 'processing' | 'published' | 'failed' | 'retrying'
  target_overrides: Record<string, any>
  post_url?: string
  post_id?: string
  error_class?: string
  error_message?: string
  created_at: string
  updated_at: string
  scheduled_post_id: number
  external_integration_id: number
  provider: ExternalIntegrationEnum
}

export interface WeekTrend {
  content: string
  external_integration: Pick<ExternalIntegration, 'id' | 'provider' | 'name' | 'picture'>
}

export type StrategicRecommendationFeature = 'topic' | 'captions' | 'images'

export interface StrategicRecommendation {
  id: number
  content: string
  recommendation: string
  feature: StrategicRecommendationFeature
  reasoning: string
  performance_metrics: string
  channel_type: 'social' | 'seo' | 'email'
  bid: string | null
  using_benchmarks: boolean
}

export interface ChannelInsight {
  content: string
  trait: string
  external_integration: Pick<ExternalIntegration, 'id' | 'provider' | 'name' | 'picture'>
  sentiment: 'positive' | 'negative'
  reasoning: string
}

export type TrackType = 'original' | 'music' | 'sfx' | 'brand_kit'

export interface AudioTrack {
  type: TrackType
  start_time: number
  duration: number
  url: string
  media_file_key: string
  provider?: string
  provider_id?: string
  title?: string
  thumbnail_url?: string
}

export interface AudioAsset {
  id: string
  title: string
  duration: number
  preview_url: string
  thumbnail_url?: string
  waveform_url?: string
  type: TrackType
  bpm?: number
  media_file_key?: string
}
